import { createApp, defineAsyncComponent } from "vue";
import { Quasar, Dark } from "quasar";
import langPt from "quasar/lang/pt";
import langEnUS from "quasar/lang/en-US";
// Every island below is a `defineAsyncComponent`, so each component becomes its
// own Rollup chunk fetched only when this page actually holds its root element
// (BUILD-001). The mount blocks are unchanged by that: Vue's async wrapper
// declares no props of its own and forwards the root vnode's props straight to
// the resolved component, so `createApp(X, { ...dataset })` still lands on X.
const AppNavbar = defineAsyncComponent(() => import("@/components/AppNavbar.vue"));
const LoginForm = defineAsyncComponent(() => import("@/components/LoginForm.vue"));
const TwoFactorVerifyForm = defineAsyncComponent(() => import("@/components/TwoFactorVerifyForm.vue"));
const TwoFactorSetupForm = defineAsyncComponent(() => import("@/components/TwoFactorSetupForm.vue"));
const PasskeyManager = defineAsyncComponent(() => import("@/components/PasskeyManager.vue"));
const FaqAccordion = defineAsyncComponent(() => import("@/components/FaqAccordion.vue"));
const CaregiverAvatar = defineAsyncComponent(() => import("@/components/CaregiverAvatar.vue"));
const RegisterCaregiverForm = defineAsyncComponent(() => import("@/components/RegisterCaregiverForm.vue"));
const RegisterCustomerForm = defineAsyncComponent(() => import("@/components/RegisterCustomerForm.vue"));
const ServiceCategoryForm = defineAsyncComponent(() => import("@/components/ServiceCategoryForm.vue"));
const CaregiverFilterForm = defineAsyncComponent(() => import("@/components/CaregiverFilterForm.vue"));
const CaregiverPickerFilterForm = defineAsyncComponent(() => import("@/components/CaregiverPickerFilterForm.vue"));
const LocationSelector = defineAsyncComponent(() => import("@/components/LocationSelector.vue"));
const ResidenceAddressSelector = defineAsyncComponent(() => import("@/components/ResidenceAddressSelector.vue"));
const CustomerAddressForm = defineAsyncComponent(() => import("@/components/CustomerAddressForm.vue"));
const UserSearchForm = defineAsyncComponent(() => import("@/components/UserSearchForm.vue"));
const EmailTemplateSearchForm = defineAsyncComponent(() => import("@/components/EmailTemplateSearchForm.vue"));
const RequestFilterForm = defineAsyncComponent(() => import("@/components/RequestFilterForm.vue"));
const ClinicalAccessFilterForm = defineAsyncComponent(() => import("@/components/ClinicalAccessFilterForm.vue"));
const ScheduleFilterForm = defineAsyncComponent(() => import("@/components/ScheduleFilterForm.vue"));
const CaregiverPaymentFilterForm = defineAsyncComponent(() => import("@/components/CaregiverPaymentFilterForm.vue"));
const CaregiverInvoiceFilterForm = defineAsyncComponent(() => import("@/components/CaregiverInvoiceFilterForm.vue"));
const DisputedVisitFilterForm = defineAsyncComponent(() => import("@/components/DisputedVisitFilterForm.vue"));
const CustomerPaymentFilterForm = defineAsyncComponent(() => import("@/components/CustomerPaymentFilterForm.vue"));
const PaginationBar = defineAsyncComponent(() => import("@/components/PaginationBar.vue"));
const HolidayForm = defineAsyncComponent(() => import("@/components/HolidayForm.vue"));
const CreateBackofficeUserForm = defineAsyncComponent(() => import("@/components/CreateBackofficeUserForm.vue"));
const CreateCaregiverForm = defineAsyncComponent(() => import("@/components/CreateCaregiverForm.vue"));
const CreateCustomerForm = defineAsyncComponent(() => import("@/components/CreateCustomerForm.vue"));
const SettingsForm = defineAsyncComponent(() => import("@/components/SettingsForm.vue"));
const CreateNextPaymentForm = defineAsyncComponent(() => import("@/components/CreateNextPaymentForm.vue"));
const HolidayFilterForm = defineAsyncComponent(() => import("@/components/HolidayFilterForm.vue"));
const CustomerRequestFilterForm = defineAsyncComponent(() => import("@/components/CustomerRequestFilterForm.vue"));
const CustomerPaymentStatusFilterForm = defineAsyncComponent(() => import("@/components/CustomerPaymentStatusFilterForm.vue"));
const DistrictFilterForm = defineAsyncComponent(() => import("@/components/DistrictFilterForm.vue"));
const MunicipalityFilterForm = defineAsyncComponent(() => import("@/components/MunicipalityFilterForm.vue"));
const LegalCompareForm = defineAsyncComponent(() => import("@/components/LegalCompareForm.vue"));
const LocalityFilterForm = defineAsyncComponent(() => import("@/components/LocalityFilterForm.vue"));
const DistrictForm = defineAsyncComponent(() => import("@/components/DistrictForm.vue"));
const MunicipalityForm = defineAsyncComponent(() => import("@/components/MunicipalityForm.vue"));
const LocalityForm = defineAsyncComponent(() => import("@/components/LocalityForm.vue"));
const ServiceFilterForm = defineAsyncComponent(() => import("@/components/ServiceFilterForm.vue"));
const ServiceForm = defineAsyncComponent(() => import("@/components/ServiceForm.vue"));
const FaqCategoryForm = defineAsyncComponent(() => import("@/components/FaqCategoryForm.vue"));
const FaqItemForm = defineAsyncComponent(() => import("@/components/FaqItemForm.vue"));
const HomeContentForm = defineAsyncComponent(() => import("@/components/HomeContentForm.vue"));
const EmailTemplateEditor = defineAsyncComponent(() => import("@/components/EmailTemplateEditor.vue"));
const ForgotPasswordForm = defineAsyncComponent(() => import("@/components/ForgotPasswordForm.vue"));
const ResetPasswordForm = defineAsyncComponent(() => import("@/components/ResetPasswordForm.vue"));
const ChangePasswordForm = defineAsyncComponent(() => import("@/components/ChangePasswordForm.vue"));
const UpcomingScheduleTimeline = defineAsyncComponent(() => import("@/components/UpcomingScheduleTimeline.vue"));
const RescindInterestForm = defineAsyncComponent(() => import("@/components/RescindInterestForm.vue"));
const WithdrawRequestForm = defineAsyncComponent(() => import("@/components/WithdrawRequestForm.vue"));
const MessageReplyForm = defineAsyncComponent(() => import("@/components/MessageReplyForm.vue"));
const NewThreadForm = defineAsyncComponent(() => import("@/components/NewThreadForm.vue"));
const CustomerMyProfileForm = defineAsyncComponent(() => import("@/components/CustomerMyProfileForm.vue"));
const CareRecipientForm = defineAsyncComponent(() => import("@/components/CareRecipientForm.vue"));
const EditCaregiverForm = defineAsyncComponent(() => import("@/components/EditCaregiverForm.vue"));
const EditCustomerForm = defineAsyncComponent(() => import("@/components/EditCustomerForm.vue"));
const AvailabilityBatchForm = defineAsyncComponent(() => import("@/components/AvailabilityBatchForm.vue"));
const CreateRequestForm = defineAsyncComponent(() => import("@/components/CreateRequestForm.vue"));
const CreateSlotForm = defineAsyncComponent(() => import("@/components/CreateSlotForm.vue"));
const CompleteProfileTextForm = defineAsyncComponent(() => import("@/components/CompleteProfileTextForm.vue"));
const CaregiverGenderSelect = defineAsyncComponent(() => import("@/components/CaregiverGenderSelect.vue"));
const InvoiceUploadForm = defineAsyncComponent(() => import("@/components/InvoiceUploadForm.vue"));
const CaregiverContractsFilterForm = defineAsyncComponent(() => import("@/components/CaregiverContractsFilterForm.vue"));
const CaregiverVisitsFilterForm = defineAsyncComponent(() => import("@/components/CaregiverVisitsFilterForm.vue"));
const VisitBatchBar = defineAsyncComponent(() => import("@/components/VisitBatchBar.vue"));
const CaregiverInvoiceDrawer = defineAsyncComponent(() => import("@/components/CaregiverInvoiceDrawer.vue"));
const CaregiverRequestsFilterForm = defineAsyncComponent(() => import("@/components/CaregiverRequestsFilterForm.vue"));
const CaregiverPaymentStatusFilterForm = defineAsyncComponent(() => import("@/components/CaregiverPaymentStatusFilterForm.vue"));
const CaregiverAvailabilityFilterForm = defineAsyncComponent(() => import("@/components/CaregiverAvailabilityFilterForm.vue"));
const DocumentUploadForm = defineAsyncComponent(() => import("@/components/DocumentUploadForm.vue"));
const PhotoUploadInput = defineAsyncComponent(() => import("@/components/PhotoUploadInput.vue"));
const LegalDocumentVersionForm = defineAsyncComponent(() => import("@/components/LegalDocumentVersionForm.vue"));
const LegalPublishForm = defineAsyncComponent(() => import("@/components/LegalPublishForm.vue"));
const AddressMapView = defineAsyncComponent(() => import("@/components/AddressMapView.vue"));
const PcFormField = defineAsyncComponent(() => import("@/components/PcFormField.vue"));
const PcFileField = defineAsyncComponent(() => import("@/components/PcFileField.vue"));
const PcCheckboxGroup = defineAsyncComponent(() => import("@/components/PcCheckboxGroup.vue"));
const ResolveVisitPanel = defineAsyncComponent(() => import("@/components/ResolveVisitPanel.vue"));
import "@/styles/app.scss";
import "@/kebab.js";
import { installConfirmInterceptor } from "@/confirm-submit.js";
import { RoleNames } from "@/identity.js";
import { LanguageCodes } from "@/languages.js";

function getQuasarLang() {
  const lang = document.documentElement.lang ?? LanguageCodes.Default;
  return lang.startsWith(LanguageCodes.English) ? langEnUS : langPt;
}

function getDarkPreference() {
  const stored = localStorage.getItem('pc-dark-mode');
  if (stored === 'dark') return true;
  if (stored === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function useQuasar(app) {
  return app.use(Quasar, {
    lang: getQuasarLang(),
    plugins: { Dark },
    config: {
      dark: getDarkPreference(),
      brand: {
        primary:   '#b33756',
        secondary: '#0d3b5c',
      },
    },
  });
}

const navbarRoot = document.querySelector("[data-vue-app-navbar]");

if (navbarRoot) {
  const d = navbarRoot.dataset;
  useQuasar(createApp(AppNavbar, {
    appName: d.appName ?? "",
    logoUrl: d.logoUrl ?? "",
    darkLogoUrl: d.darkLogoUrl ?? "",
    lang: d.lang ?? LanguageCodes.Default,
    isAuthenticated: d.isAuthenticated ?? "false",
    userName: d.userName ?? "",
    isAdmin: d.isAdmin ?? "false",
    userRole: d.userRole ?? "",
    publicAccess: d.publicAccess ?? "false",
    loginEnabled: d.loginEnabled ?? "false",
    caregiverStatus: d.caregiverStatus ?? "",
    pendingApprovalsCount: d.pendingApprovalsCount ?? "0",
    caregiverDashboardUrl: d.caregiverDashboardUrl ?? "",
    caregiverPendingCount: d.caregiverPendingCount ?? "0",
    antiforgeryToken: d.antiforgeryToken ?? "",
    homeUrl: d.homeUrl ?? "/",
    caregiversUrl: d.caregiversUrl ?? "",
    faqUrl: d.faqUrl ?? "",
    backofficeUrl: d.backofficeUrl ?? "",
    loginUrl: d.loginUrl ?? "",
    myAccountUrl: d.myAccountUrl ?? "",
    setLanguageUrl: d.setLanguageUrl ?? "",
    logoutUrl: d.logoutUrl ?? "",
    currentPath: d.currentPath ?? "/",
    completeProfileUrl: d.completeProfileUrl ?? "",
    availabilityUrl: d.availabilityUrl ?? "",
    contractsUrl: d.contractsUrl ?? "",
    paymentsUrl: d.paymentsUrl ?? "",
    messagesUrl: d.messagesUrl ?? "",
    visitsUrl: d.visitsUrl ?? "",
    labelHome: d.labelHome ?? "",
    labelCaregivers: d.labelCaregivers ?? "",
    labelFaq: d.labelFaq ?? "",
    labelBackoffice: d.labelBackoffice ?? "",
    labelLogin: d.labelLogin ?? "",
    labelLogout: d.labelLogout ?? "",
    labelSwitchToDarkMode: d.labelSwitchToDarkMode ?? "",
    labelSwitchToLightMode: d.labelSwitchToLightMode ?? "",
    labelMyAccount: d.labelMyAccount ?? "",
    labelMyProfile: d.labelMyProfile ?? "",
    labelCompleteProfile: d.labelCompleteProfile ?? "",
    labelMyArea: d.labelMyArea ?? "",
    labelAvailability: d.labelAvailability ?? "",
    labelContracts: d.labelContracts ?? "",
    labelPayments: d.labelPayments ?? "",
    labelMessages: d.labelMessages ?? "",
    labelVisits: d.labelVisits ?? "",
    customerDashboardUrl: d.customerDashboardUrl ?? "",
    customerPendingCount: d.customerPendingCount ?? "0",
    customerProfileUrl: d.customerProfileUrl ?? "",
    labelCustomerMyArea: d.labelCustomerMyArea ?? "",
  })).mount(navbarRoot);
}

document.querySelectorAll("[data-vue-caregiver-avatar]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(CaregiverAvatar, {
    photoUrl: d.photoUrl ?? "",
    firstName: d.firstName ?? "",
    lastName: d.lastName ?? "",
  })).mount(el);
});

const faqAccordionRoot = document.querySelector("[data-vue-faq-accordion]");

if (faqAccordionRoot) {
  const d = faqAccordionRoot.dataset;
  useQuasar(createApp(FaqAccordion, {
    categoriesJson: d.categoriesJson ?? "[]",
    itemsJson: d.itemsJson ?? "[]",
    lang: d.lang ?? LanguageCodes.Default,
    emptyLabel: d.emptyLabel ?? "",
  })).mount(faqAccordionRoot);
}

const registerCaregiverRoot = document.querySelector("[data-vue-register-caregiver-form]");

if (registerCaregiverRoot) {
  const d = registerCaregiverRoot.dataset;
  useQuasar(createApp(RegisterCaregiverForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    titleLabel: d.titleLabel ?? "",
    firstNameLabel: d.firstNameLabel ?? "",
    lastNameLabel: d.lastNameLabel ?? "",
    emailLabel: d.emailLabel ?? "",
    phoneLabel: d.phoneLabel ?? "",
    genderLabel: d.genderLabel ?? "",
    birthDateLabel: d.birthDateLabel ?? "",
    vatLabel: d.vatLabel ?? "",
    passwordLabel: d.passwordLabel ?? "",
    confirmPasswordLabel: d.confirmPasswordLabel ?? "",
    labelFemale: d.labelFemale ?? "",
    labelMale: d.labelMale ?? "",
    labelOther: d.labelOther ?? "",
    submitLabel: d.submitLabel ?? "",
    loginLabel: d.loginLabel ?? "",
    loginUrl: d.loginUrl ?? "",
    alreadyHaveAccountLabel: d.alreadyHaveAccountLabel ?? "",
    requiredMsg: d.requiredMsg ?? "",
    invalidEmailMsg: d.invalidEmailMsg ?? "",
    passwordTooShortMsg: d.passwordTooShortMsg ?? "",
    passwordMismatchMsg: d.passwordMismatchMsg ?? "",
    minLength: Number(d.passwordMinLength ?? 12),
    passwordHintMsg: d.passwordHintMsg ?? "",
    strengthLabels: JSON.parse(d.passwordStrengthLabels ?? "[]"),
    birthDateInFutureMsg: d.birthDateInFutureMsg ?? "",
    initialFirstName: d.initialFirstName ?? "",
    initialLastName: d.initialLastName ?? "",
    initialEmail: d.initialEmail ?? "",
    initialPhone: d.initialPhone ?? "",
    initialGender: d.initialGender ?? "",
    initialBirthDate: d.initialBirthDate ?? "",
    initialVatNumber: d.initialVatNumber ?? "",
    errors: d.errors ?? "{}",
    acceptLabel: d.acceptLabel ?? "",
    caregiverTermsUrl: d.caregiverTermsUrl ?? "/legal/caregiver-terms",
    privacyUrl: d.privacyUrl ?? "/legal/privacy",
    termsLinkLabel: d.termsLinkLabel ?? "",
    privacyLinkLabel: d.privacyLinkLabel ?? "",
    viewLinkLabel: d.viewLinkLabel ?? "",
    mustAcceptMsg: d.mustAcceptMsg ?? "",
  })).mount(registerCaregiverRoot);
}

const registerCustomerRoot = document.querySelector("[data-vue-register-customer-form]");

if (registerCustomerRoot) {
  const d = registerCustomerRoot.dataset;
  useQuasar(createApp(RegisterCustomerForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    municipalitiesJson:    d.municipalitiesJson    ?? "[]",
    titleLabel:            d.titleLabel            ?? "",
    billingNotice:         d.billingNotice         ?? "",
    firstNameLabel:        d.firstNameLabel        ?? "",
    lastNameLabel:         d.lastNameLabel         ?? "",
    emailLabel:            d.emailLabel            ?? "",
    phoneLabel:            d.phoneLabel            ?? "",
    genderLabel:           d.genderLabel           ?? "",
    birthDateLabel:        d.birthDateLabel        ?? "",
    vatLabel:              d.vatLabel              ?? "",
    addressLabel:          d.addressLabel          ?? "",
    address2Label:         d.address2Label         ?? "",
    postalCodeLabel:       d.postalCodeLabel       ?? "",
    invalidPostalCodeMsg:  d.invalidPostalCodeMsg  ?? "",
    districtLabel:         d.districtLabel         ?? "",
    municipalityLabel:     d.municipalityLabel     ?? "",
    passwordLabel:         d.passwordLabel         ?? "",
    confirmPasswordLabel:  d.confirmPasswordLabel  ?? "",
    labelFemale:           d.labelFemale           ?? "",
    labelMale:             d.labelMale             ?? "",
    labelOther:            d.labelOther            ?? "",
    submitLabel:           d.submitLabel           ?? "",
    loginLabel:            d.loginLabel            ?? "",
    loginUrl:              d.loginUrl              ?? "",
    alreadyHaveAccountLabel: d.alreadyHaveAccountLabel ?? "",
    requiredMsg:           d.requiredMsg           ?? "",
    invalidEmailMsg:       d.invalidEmailMsg       ?? "",
    passwordTooShortMsg:   d.passwordTooShortMsg   ?? "",
    passwordMismatchMsg:   d.passwordMismatchMsg   ?? "",
    minLength:             Number(d.passwordMinLength ?? 12),
    passwordHintMsg:       d.passwordHintMsg       ?? "",
    strengthLabels:        JSON.parse(d.passwordStrengthLabels ?? "[]"),
    birthDateInFutureMsg:  d.birthDateInFutureMsg  ?? "",
    regionsJson:           d.regionsJson           ?? "[]",
    initialFirstName:      d.initialFirstName      ?? "",
    initialLastName:       d.initialLastName       ?? "",
    initialEmail:          d.initialEmail          ?? "",
    initialPhone:          d.initialPhone          ?? "",
    initialGender:         d.initialGender         ?? "",
    initialBirthDate:      d.initialBirthDate      ?? "",
    initialVatNumber:      d.initialVatNumber      ?? "",
    initialAddressLine1:   d.initialAddressLine1   ?? "",
    initialAddressLine2:   d.initialAddressLine2   ?? "",
    initialPostalCode:     d.initialPostalCode     ?? "",
    initialDistrictId:     d.initialDistrictId     ?? "",
    initialMunicipalityId: d.initialMunicipalityId ?? "",
    errors: d.errors ?? "{}",
    acceptLabel:      d.acceptLabel      ?? "",
    customerTermsUrl: d.customerTermsUrl ?? "/legal/customer-terms",
    privacyUrl:       d.privacyUrl       ?? "/legal/privacy",
    termsLinkLabel:   d.termsLinkLabel   ?? "",
    privacyLinkLabel: d.privacyLinkLabel ?? "",
    viewLinkLabel:    d.viewLinkLabel    ?? "",
    mustAcceptMsg:    d.mustAcceptMsg    ?? "",
    labelLocate:      d.labelLocate      ?? "",
    labelMapHint:     d.labelMapHint     ?? "",
  })).mount(registerCustomerRoot);
}

const serviceCategoryFormRoot = document.querySelector("[data-vue-service-category-form]");

if (serviceCategoryFormRoot) {
  const d = serviceCategoryFormRoot.dataset;
  useQuasar(createApp(ServiceCategoryForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    cancelUrl: d.cancelUrl ?? "",
    formJson: d.formJson ?? "{}",
    labelCategoryName: d.labelCategoryName ?? "",
    labelDescription: d.labelDescription ?? "",
    labelIcon: d.labelIcon ?? "",
    labelIconHint: d.labelIconHint ?? "",
    labelSortOrder: d.labelSortOrder ?? "",
    labelStatus: d.labelStatus ?? "",
    labelActive: d.labelActive ?? "",
    labelTranslations: d.labelTranslations ?? "",
    labelSave: d.labelSave ?? "",
    labelCancel: d.labelCancel ?? "",
    requiredMsg: d.requiredMsg ?? "",
    errors: d.errors ?? "[]",
  })).mount(serviceCategoryFormRoot);
}

const serviceFilterFormRoot = document.querySelector("[data-vue-service-filter-form]");

if (serviceFilterFormRoot) {
  const d = serviceFilterFormRoot.dataset;
  useQuasar(createApp(ServiceFilterForm, {
    actionUrl:             d.actionUrl             ?? "",
    initialSearch:         d.initialSearch         ?? "",
    initialCategoryId:     d.initialCategoryId     ?? "",
    categoryOptions:       d.categoryOptions       ?? "[]",
    labelSearch:           d.labelSearch           ?? "",
    labelFilterByCategory: d.labelFilterByCategory ?? "",
    labelFilter:           d.labelFilter           ?? "",
  })).mount(serviceFilterFormRoot);
}

const serviceFormRoot = document.querySelector("[data-vue-service-form]");

if (serviceFormRoot) {
  const d = serviceFormRoot.dataset;
  useQuasar(createApp(ServiceForm, {
    actionUrl:        d.actionUrl        ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    cancelUrl:        d.cancelUrl        ?? "",
    formJson:         d.formJson         ?? "{}",
    categories:       d.categories       ?? "[]",
    labelServiceName: d.labelServiceName ?? "",
    labelCategory:    d.labelCategory    ?? "",
    labelDescription: d.labelDescription ?? "",
    labelIcon:        d.labelIcon        ?? "",
    labelIconHint:    d.labelIconHint    ?? "",
    labelSortOrder:   d.labelSortOrder   ?? "",
    labelStatus:      d.labelStatus      ?? "",
    labelActive:      d.labelActive      ?? "",
    labelTranslations:d.labelTranslations?? "",
    labelSave:        d.labelSave        ?? "",
    labelCancel:      d.labelCancel      ?? "",
    requiredMsg:      d.requiredMsg      ?? "",
    errors:           d.errors           ?? "[]",
  })).mount(serviceFormRoot);
}

const faqCategoryFormRoot = document.querySelector("[data-vue-faq-category-form]");

if (faqCategoryFormRoot) {
  const d = faqCategoryFormRoot.dataset;
  useQuasar(createApp(FaqCategoryForm, {
    actionUrl:          d.actionUrl          ?? "",
    antiforgeryToken:   d.antiforgeryToken   ?? "",
    cancelUrl:          d.cancelUrl          ?? "",
    formJson:           d.formJson           ?? "{}",
    primaryLanguageId:  Number(d.primaryLanguageId ?? 0),
    audienceOptions:    d.audienceOptions    ?? "[]",
    labelTitle:         d.labelTitle         ?? "",
    labelTargetAudience:d.labelTargetAudience?? "",
    labelSortOrder:     d.labelSortOrder     ?? "",
    labelActive:        d.labelActive        ?? "",
    labelTranslations:  d.labelTranslations  ?? "",
    labelSave:          d.labelSave          ?? "",
    labelCancel:        d.labelCancel        ?? "",
    requiredMsg:        d.requiredMsg        ?? "",
    errors:             d.errors             ?? "[]",
  })).mount(faqCategoryFormRoot);
}

const faqItemFormRoot = document.querySelector("[data-vue-faq-item-form]");

if (faqItemFormRoot) {
  const d = faqItemFormRoot.dataset;
  useQuasar(createApp(FaqItemForm, {
    actionUrl:         d.actionUrl         ?? "",
    antiforgeryToken:  d.antiforgeryToken  ?? "",
    cancelUrl:         d.cancelUrl         ?? "",
    formJson:          d.formJson          ?? "{}",
    primaryLanguageId: Number(d.primaryLanguageId ?? 0),
    categories:        d.categories        ?? "[]",
    labelCategory:     d.labelCategory     ?? "",
    labelQuestion:     d.labelQuestion     ?? "",
    labelAnswer:       d.labelAnswer       ?? "",
    labelSortOrder:    d.labelSortOrder    ?? "",
    labelActive:       d.labelActive       ?? "",
    labelTranslations: d.labelTranslations ?? "",
    labelSave:         d.labelSave         ?? "",
    labelCancel:       d.labelCancel       ?? "",
    requiredMsg:       d.requiredMsg       ?? "",
    errors:            d.errors            ?? "[]",
  })).mount(faqItemFormRoot);
}

const homeContentFormRoot = document.querySelector("[data-vue-home-content-form]");

if (homeContentFormRoot) {
  const d = homeContentFormRoot.dataset;
  useQuasar(createApp(HomeContentForm, {
    actionUrl:            d.actionUrl            ?? "",
    previewUrl:            d.previewUrl           ?? "",
    mediaUrl:              d.mediaUrl             ?? "",
    antiforgeryToken:      d.antiforgeryToken     ?? "",
    formJson:              d.formJson             ?? "{}",
    libraryJson:           d.libraryJson          ?? "[]",
    labelTextSection:      d.labelTextSection     ?? "",
    labelImageSection:     d.labelImageSection    ?? "",
    labelMediaLibrary:     d.labelMediaLibrary    ?? "",
    labelEmptyResetsHint:  d.labelEmptyResetsHint ?? "",
    labelUseDefaultImage:  d.labelUseDefaultImage ?? "",
    labelPreview:          d.labelPreview         ?? "",
    labelSave:             d.labelSave            ?? "",
    labelBlocksSection:      d.labelBlocksSection      ?? "",
    labelBlockEnabled:       d.labelBlockEnabled       ?? "",
    labelBlockStrategy:      d.labelBlockStrategy      ?? "",
    labelBlockItemCount:     d.labelBlockItemCount     ?? "",
    labelBlockAddItem:       d.labelBlockAddItem       ?? "",
    labelBlockNotEligible:   d.labelBlockNotEligible   ?? "",
    labelBlockMoveUp:        d.labelBlockMoveUp        ?? "",
    labelBlockMoveDown:      d.labelBlockMoveDown      ?? "",
    labelBlockRemoveItem:    d.labelBlockRemoveItem    ?? "",
    labelBlockSlotsFilled:   d.labelBlockSlotsFilled   ?? "",
  })).mount(homeContentFormRoot);
}

document.querySelectorAll("[data-vue-location-selector]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(LocationSelector, {
    districtsJson:           d.districtsJson           ?? "[]",
    municipalitiesJson:      d.municipalitiesJson      ?? "[]",
    initialDistrictIds:      d.initialDistrictIds      ?? "[]",
    initialMunicipalityIds:  d.initialMunicipalityIds  ?? "[]",
    labelDistricts:          d.labelDistricts          ?? "",
    labelMunicipalities:     d.labelMunicipalities     ?? "",
  })).mount(el);
});

document.querySelectorAll("[data-vue-residence-address]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(ResidenceAddressSelector, {
    districtsJson:           d.districtsJson           ?? "[]",
    municipalitiesJson:      d.municipalitiesJson      ?? "[]",
    initialDistrictId:       d.initialDistrictId       ?? "",
    initialMunicipalityId:   d.initialMunicipalityId   ?? "",
    initialAddressLine1:     d.initialAddressLine1     ?? "",
    initialAddressLine2:     d.initialAddressLine2     ?? "",
    initialPostalCode:       d.initialPostalCode       ?? "",
    labelDistrict:           d.labelDistrict           ?? "",
    labelMunicipality:       d.labelMunicipality       ?? "",
    labelAddressLine1:       d.labelAddressLine1       ?? "",
    labelAddressLine2:       d.labelAddressLine2       ?? "",
    labelPostalCode:         d.labelPostalCode         ?? "",
    invalidPostalCodeMsg:    d.invalidPostalCodeMsg    ?? "",
    postalCodeError:         d.postalCodeError         ?? "",
    initialLatitude:         d.initialLatitude         ?? "",
    initialLongitude:        d.initialLongitude        ?? "",
    labelLocate:             d.labelLocate             ?? "",
    labelMapHint:            d.labelMapHint            ?? "",
  })).mount(el);
});

document.querySelectorAll("[data-vue-customer-address-form]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(CustomerAddressForm, {
    actionUrl:             d.actionUrl             ?? "",
    cancelUrl:             d.cancelUrl             ?? "",
    antiforgeryToken:      d.antiforgeryToken      ?? "",
    initialId:             d.initialId             ?? "",
    districtsJson:         d.districtsJson         ?? "[]",
    municipalitiesJson:    d.municipalitiesJson    ?? "[]",
    initialLabel:          d.initialLabel          ?? "",
    initialAddressLine1:   d.initialAddressLine1   ?? "",
    initialAddressLine2:   d.initialAddressLine2   ?? "",
    initialPostalCode:     d.initialPostalCode     ?? "",
    initialDistrictId:     d.initialDistrictId     ?? "",
    initialMunicipalityId: d.initialMunicipalityId ?? "",
    initialLatitude:       d.initialLatitude       ?? "",
    initialLongitude:      d.initialLongitude      ?? "",
    labelLabel:            d.labelLabel            ?? "",
    labelAddressLine1:     d.labelAddressLine1     ?? "",
    labelAddressLine2:     d.labelAddressLine2     ?? "",
    labelPostalCode:       d.labelPostalCode       ?? "",
    labelDistrict:         d.labelDistrict         ?? "",
    labelMunicipality:     d.labelMunicipality     ?? "",
    labelSave:             d.labelSave             ?? "",
    labelCancel:           d.labelCancel           ?? "",
    labelLocate:           d.labelLocate           ?? "",
    labelMapHint:          d.labelMapHint          ?? "",
    labelRequiredMsg:      d.labelRequiredMsg      ?? "",
    invalidPostalCodeMsg:  d.invalidPostalCodeMsg  ?? "",
  })).mount(el);
});

const caregiverFilterRoot = document.querySelector("[data-vue-caregiver-filter-form]");

if (caregiverFilterRoot) {
  const d = caregiverFilterRoot.dataset;
  useQuasar(createApp(CaregiverFilterForm, {
    actionUrl:             d.actionUrl             ?? "",
    getMunicipalitiesUrl:  d.getMunicipalitiesUrl  ?? "",
    districtsJson:         d.districtsJson         ?? "[]",
    servicesJson:          d.servicesJson          ?? "[]",
    languagesJson:         d.languagesJson         ?? "[]",
    labelDistrict:         d.labelDistrict         ?? "",
    labelMunicipality:     d.labelMunicipality     ?? "",
    labelService:          d.labelService          ?? "",
    labelGender:           d.labelGender           ?? "",
    labelLanguage:         d.labelLanguage         ?? "",
    labelSearch:           d.labelSearch           ?? "",
    labelFemale:           d.labelFemale           ?? "",
    labelMale:             d.labelMale             ?? "",
    initialDistrictId:     d.initialDistrictId     ?? "",
    initialMunicipalityId: d.initialMunicipalityId ?? "",
    initialServiceId:      d.initialServiceId      ?? "",
    initialGender:         d.initialGender         ?? "",
    initialLanguageId:     d.initialLanguageId     ?? "",
  })).mount(caregiverFilterRoot);
}

const caregiverPickerFilterRoot = document.querySelector("[data-vue-caregiver-picker-filter-form]");

if (caregiverPickerFilterRoot) {
  const d = caregiverPickerFilterRoot.dataset;
  useQuasar(createApp(CaregiverPickerFilterForm, {
    actionUrl:             d.actionUrl             ?? "",
    municipalitiesJson:    d.municipalitiesJson    ?? "[]",
    servicesJson:          d.servicesJson          ?? "[]",
    labelName:             d.labelName             ?? "",
    labelMunicipality:     d.labelMunicipality     ?? "",
    labelService:          d.labelService          ?? "",
    labelFilter:           d.labelFilter           ?? "",
    initialName:           d.initialName           ?? "",
    initialMunicipalityId: d.initialMunicipalityId ?? "",
    initialServiceId:      d.initialServiceId      ?? "",
  })).mount(caregiverPickerFilterRoot);
}

const userSearchRoot = document.querySelector("[data-vue-user-search-form]");

if (userSearchRoot) {
  const d = userSearchRoot.dataset;
  useQuasar(createApp(UserSearchForm, {
    actionUrl:           d.actionUrl ?? "",
    initialSearch:       d.initialSearch ?? "",
    initialUserType:     d.initialUserType ?? "",
    showTypeFilter:      d.showTypeFilter === "true",
    labelSearch:         d.labelSearch ?? "",
    labelFilter:         d.labelFilter ?? "",
    labelUserType:       d.labelUserType ?? "",
    labelAllTypes:       d.labelAllTypes ?? "",
    labelCaregiver:      d.labelCaregiver ?? "",
    labelCustomer:       d.labelCustomer ?? "",
    labelBackofficeUser: d.labelBackofficeUser ?? "",
    labelClearFilter:    d.labelClearFilter ?? "",
    showRegisteredFilter:  d.showRegisteredFilter === "true",
    initialRegistered:     d.initialRegistered ?? "",
    registeredOptionsJson: d.registeredOptionsJson ?? "[]",
    labelRegistered:       d.labelRegistered ?? "",
    showStatusFilter:      d.showStatusFilter === "true",
    initialStatus:         d.initialStatus ?? "",
    statusOptionsJson:     d.statusOptionsJson ?? "[]",
    labelStatus:           d.labelStatus ?? "",
  })).mount(userSearchRoot);
}

const emailTemplateSearchRoot = document.querySelector("[data-vue-email-template-search-form]");

if (emailTemplateSearchRoot) {
  const d = emailTemplateSearchRoot.dataset;
  useQuasar(createApp(EmailTemplateSearchForm, {
    actionUrl:           d.actionUrl ?? "",
    initialSearch:       d.initialSearch ?? "",
    initialStatus:       d.initialStatus ?? "",
    labelSearch:         d.labelSearch ?? "",
    labelFilter:         d.labelFilter ?? "",
    labelFilterByStatus: d.labelFilterByStatus ?? "",
    labelAllStatuses:    d.labelAllStatuses ?? "",
    labelActive:         d.labelActive ?? "",
    labelInactive:       d.labelInactive ?? "",
    labelClearFilter:    d.labelClearFilter ?? "",
  })).mount(emailTemplateSearchRoot);
}

const requestFilterRoot = document.querySelector("[data-vue-request-filter-form]");

if (requestFilterRoot) {
  const d = requestFilterRoot.dataset;
  useQuasar(createApp(RequestFilterForm, {
    actionUrl:         d.actionUrl ?? "",
    initialSearch:     d.initialSearch ?? "",
    initialStatus:     d.initialStatus ?? "",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelSearch:       d.labelSearch ?? "",
    labelStatus:       d.labelStatus ?? "",
    labelFilter:       d.labelFilter ?? "",
    labelClearFilter:  d.labelClearFilter ?? "",
  })).mount(requestFilterRoot);
}

const clinicalAccessFilterRoot = document.querySelector("[data-vue-clinical-access-filter-form]");

if (clinicalAccessFilterRoot) {
  const d = clinicalAccessFilterRoot.dataset;
  useQuasar(createApp(ClinicalAccessFilterForm, {
    actionUrl:          d.actionUrl ?? "",
    initialSearch:      d.initialSearch ?? "",
    initialRole:        d.initialRole ?? "",
    initialOutcome:     d.initialOutcome ?? "",
    initialContext:     d.initialContext ?? "",
    initialFromDate:    d.initialFromDate ?? "",
    initialToDate:      d.initialToDate ?? "",
    roleOptionsJson:    d.roleOptionsJson ?? "[]",
    outcomeOptionsJson: d.outcomeOptionsJson ?? "[]",
    contextOptionsJson: d.contextOptionsJson ?? "[]",
    labelSearch:        d.labelSearch ?? "",
    labelRole:          d.labelRole ?? "",
    labelOutcome:       d.labelOutcome ?? "",
    labelContext:       d.labelContext ?? "",
    labelFromDate:      d.labelFromDate ?? "",
    labelToDate:        d.labelToDate ?? "",
    labelFilter:        d.labelFilter ?? "",
  })).mount(clinicalAccessFilterRoot);
}

const scheduleFilterRoot = document.querySelector("[data-vue-schedule-filter-form]");

if (scheduleFilterRoot) {
  const d = scheduleFilterRoot.dataset;
  useQuasar(createApp(ScheduleFilterForm, {
    actionUrl:          d.actionUrl ?? "",
    initialCaregiverId: d.initialCaregiverId ?? "",
    initialCustomerId:  d.initialCustomerId ?? "",
    caregiversJson:     d.caregiversJson ?? "[]",
    customersJson:      d.customersJson ?? "[]",
    labelCaregiver:     d.labelCaregiver ?? "",
    labelCustomer:      d.labelCustomer ?? "",
    labelFilter:        d.labelFilter ?? "",
    labelClearFilter:   d.labelClearFilter ?? "",
  })).mount(scheduleFilterRoot);
}

const caregiverPaymentFilterRoot = document.querySelector("[data-vue-caregiver-payment-filter-form]");

if (caregiverPaymentFilterRoot) {
  const d = caregiverPaymentFilterRoot.dataset;
  useQuasar(createApp(CaregiverPaymentFilterForm, {
    actionUrl:          d.actionUrl ?? "",
    initialCaregiverId: d.initialCaregiverId ?? "",
    initialStatus:       d.initialStatus ?? "",
    initialHoldKind:     d.initialHoldKind ?? "",
    holdKindOptionsJson: d.holdKindOptionsJson ?? "[]",
    labelHoldKind:       d.labelHoldKind ?? "",
    caregiversJson:     d.caregiversJson ?? "[]",
    statusOptionsJson:  d.statusOptionsJson ?? "[]",
    labelCaregiver:     d.labelCaregiver ?? "",
    labelStatus:        d.labelStatus ?? "",
    labelFilter:        d.labelFilter ?? "",
    labelClearFilter:   d.labelClearFilter ?? "",
  })).mount(caregiverPaymentFilterRoot);
}

const caregiverInvoiceFilterRoot = document.querySelector("[data-vue-caregiver-invoice-filter-form]");

if (caregiverInvoiceFilterRoot) {
  const d = caregiverInvoiceFilterRoot.dataset;
  useQuasar(createApp(CaregiverInvoiceFilterForm, {
    actionUrl:             d.actionUrl ?? "",
    initialCaregiverId:    d.initialCaregiverId ?? "",
    initialContractNumber: d.initialContractNumber ?? "",
    initialStatus:         d.initialStatus ?? "",
    caregiversJson:        d.caregiversJson ?? "[]",
    statusOptionsJson:     d.statusOptionsJson ?? "[]",
    labelCaregiver:        d.labelCaregiver ?? "",
    labelContractNumber:   d.labelContractNumber ?? "",
    labelStatus:           d.labelStatus ?? "",
    labelFilter:           d.labelFilter ?? "",
  })).mount(caregiverInvoiceFilterRoot);
}

const disputedVisitFilterRoot = document.querySelector("[data-vue-disputed-visit-filter-form]");

if (disputedVisitFilterRoot) {
  const d = disputedVisitFilterRoot.dataset;
  useQuasar(createApp(DisputedVisitFilterForm, {
    actionUrl:            d.actionUrl ?? "",
    initialFlaggedByRole: d.initialFlaggedByRole ?? "",
    roleOptionsJson:      d.roleOptionsJson ?? "[]",
    labelFlaggedBy:       d.labelFlaggedBy ?? "",
    labelFilter:          d.labelFilter ?? "",
  })).mount(disputedVisitFilterRoot);
}

const customerPaymentFilterRoot = document.querySelector("[data-vue-customer-payment-filter-form]");

if (customerPaymentFilterRoot) {
  const d = customerPaymentFilterRoot.dataset;
  useQuasar(createApp(CustomerPaymentFilterForm, {
    actionUrl:         d.actionUrl ?? "",
    initialCustomerId: d.initialCustomerId ?? "",
    initialStatus:     d.initialStatus ?? "",
    customersJson:     d.customersJson ?? "[]",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelCustomer:     d.labelCustomer ?? "",
    labelStatus:       d.labelStatus ?? "",
    labelFilter:       d.labelFilter ?? "",
    labelClearFilter:  d.labelClearFilter ?? "",
  })).mount(customerPaymentFilterRoot);
}

const emailTemplateEditorRoot = document.querySelector("[data-vue-email-template-editor]");

if (emailTemplateEditorRoot) {
  const d = emailTemplateEditorRoot.dataset;
  useQuasar(createApp(EmailTemplateEditor, {
    actionUrl:                  d.actionUrl ?? "",
    cancelUrl:                  d.cancelUrl ?? "",
    antiforgeryToken:           d.antiforgeryToken ?? "",
    templateId:                 parseInt(d.templateId ?? "0"),
    translationsJson:           d.translationsJson ?? "[]",
    placeholdersJson:           d.placeholdersJson ?? "[]",
    labelSubject:               d.labelSubject ?? "",
    labelHtmlBody:              d.labelHtmlBody ?? "",
    labelTranslations:          d.labelTranslations ?? "",
    labelAvailablePlaceholders: d.labelAvailablePlaceholders ?? "",
    labelPlaceholderHint:       d.labelPlaceholderHint ?? "",
    labelSave:                  d.labelSave ?? "",
    labelCancel:                d.labelCancel ?? "",
    labelRequired:              d.labelRequired ?? "",
  })).mount(emailTemplateEditorRoot);
}

const loginFormRoot = document.querySelector("[data-vue-login-form]");

if (loginFormRoot) {
  const d = loginFormRoot.dataset;
  useQuasar(createApp(LoginForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    returnUrl: d.returnUrl ?? "",
    initialEmail: d.initialEmail ?? "",
    emailLabel: d.emailLabel ?? "",
    passwordLabel: d.passwordLabel ?? "",
    rememberMeLabel: d.rememberMeLabel ?? "",
    submitLabel: d.submitLabel ?? "",
    forgotPasswordLabel: d.forgotPasswordLabel ?? "",
    forgotPasswordUrl: d.forgotPasswordUrl ?? "",
    becomeCaregiverLabel: d.becomeCaregiverLabel ?? "",
    becomeCaregiverUrl: d.becomeCaregiverUrl ?? "",
    registerCustomerLabel: d.registerCustomerLabel ?? "",
    registerCustomerUrl: d.registerCustomerUrl ?? "",
    registrationEnabled: d.registrationEnabled ?? "false",
    requiredMsg: d.requiredMsg ?? "",
    invalidEmailMsg: d.invalidEmailMsg ?? "",
    errors: d.errors ?? "[]"
  })).mount(loginFormRoot);
}

const twoFactorVerifyRoot = document.querySelector("[data-vue-twofactor-verify-form]");

if (twoFactorVerifyRoot) {
  const d = twoFactorVerifyRoot.dataset;
  useQuasar(createApp(TwoFactorVerifyForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    returnUrl: d.returnUrl ?? "",
    rememberMe: d.rememberMe ?? "false",
    title: d.title ?? "",
    enterCodeLabel: d.enterCodeLabel ?? "",
    enterRecoveryLabel: d.enterRecoveryLabel ?? "",
    codeLabel: d.codeLabel ?? "",
    recoveryCodeLabel: d.recoveryCodeLabel ?? "",
    rememberMachineLabel: d.rememberMachineLabel ?? "",
    verifyLabel: d.verifyLabel ?? "",
    useRecoveryLabel: d.useRecoveryLabel ?? "",
    useAuthenticatorLabel: d.useAuthenticatorLabel ?? "",
    requiredMsg: d.requiredMsg ?? "",
    errors: d.errors ?? "[]",
    hasPasskeys: d.hasPasskeys ?? "false",
    hasAuthenticator: d.hasAuthenticator ?? "false",
    wasRecovery: d.wasRecovery ?? "false",
    hasErrors: d.hasErrors ?? "false",
    passkeyOptionsUrl: d.passkeyOptionsUrl ?? "",
    passkeyLoginUrl: d.passkeyLoginUrl ?? "",
    usePasskeyLabel: d.usePasskeyLabel ?? "",
    passkeyPromptLabel: d.passkeyPromptLabel ?? "",
    passkeyErrorLabel: d.passkeyErrorLabel ?? ""
  })).mount(twoFactorVerifyRoot);
}

const twoFactorSetupRoot = document.querySelector("[data-vue-twofactor-setup-form]");

if (twoFactorSetupRoot) {
  const d = twoFactorSetupRoot.dataset;
  useQuasar(createApp(TwoFactorSetupForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    qrCodeDataUri: d.qrCodeDataUri ?? "",
    sharedKey: d.sharedKey ?? "",
    sharedKeyRaw: d.sharedKeyRaw ?? "",
    title: d.title ?? "",
    scanInstructions: d.scanInstructions ?? "",
    manualKeyLabel: d.manualKeyLabel ?? "",
    codeLabel: d.codeLabel ?? "",
    verifyLabel: d.verifyLabel ?? "",
    requiredMsg: d.requiredMsg ?? ""
  })).mount(twoFactorSetupRoot);
}

const passkeyManagerRoot = document.querySelector("[data-vue-passkey-manager]");

if (passkeyManagerRoot) {
  const d = passkeyManagerRoot.dataset;
  useQuasar(createApp(PasskeyManager, {
    optionsUrl: d.optionsUrl ?? "",
    registerUrl: d.registerUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    nameLabel: d.nameLabel ?? "",
    addPlatformLabel: d.addPlatformLabel ?? "",
    addCrossPlatformLabel: d.addCrossPlatformLabel ?? "",
    addHint: d.addHint ?? "",
    unsupportedLabel: d.unsupportedLabel ?? "",
    errorLabel: d.errorLabel ?? ""
  })).mount(passkeyManagerRoot);
}

const forgotPasswordFormRoot = document.querySelector("[data-vue-forgot-password-form]");

if (forgotPasswordFormRoot) {
  const d = forgotPasswordFormRoot.dataset;
  useQuasar(createApp(ForgotPasswordForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    titleLabel: d.titleLabel ?? "",
    instructionsLabel: d.instructionsLabel ?? "",
    emailLabel: d.emailLabel ?? "",
    submitLabel: d.submitLabel ?? "",
    backToLoginLabel: d.backToLoginLabel ?? "",
    loginUrl: d.loginUrl ?? "",
    requiredMsg: d.requiredMsg ?? "",
    invalidEmailMsg: d.invalidEmailMsg ?? "",
    initialEmail: d.initialEmail ?? "",
    errors: d.errors ?? "{}"
  })).mount(forgotPasswordFormRoot);
}

const resetPasswordFormRoot = document.querySelector("[data-vue-reset-password-form]");

if (resetPasswordFormRoot) {
  const d = resetPasswordFormRoot.dataset;
  useQuasar(createApp(ResetPasswordForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    userId: d.userId ?? "",
    token: d.token ?? "",
    titleLabel: d.titleLabel ?? "",
    instructionsLabel: d.instructionsLabel ?? "",
    newPasswordLabel: d.newPasswordLabel ?? "",
    confirmNewPasswordLabel: d.confirmNewPasswordLabel ?? "",
    submitLabel: d.submitLabel ?? "",
    backToLoginLabel: d.backToLoginLabel ?? "",
    loginUrl: d.loginUrl ?? "",
    requiredMsg: d.requiredMsg ?? "",
    passwordTooShortMsg: d.passwordTooShortMsg ?? "",
    passwordMismatchMsg: d.passwordMismatchMsg ?? "",
    minLength: Number(d.passwordMinLength ?? 12),
    passwordHintMsg: d.passwordHintMsg ?? "",
    strengthLabels: JSON.parse(d.passwordStrengthLabels ?? "[]"),
    errors: d.errors ?? "{}"
  })).mount(resetPasswordFormRoot);
}

document.querySelectorAll("[data-vue-change-password-form]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(ChangePasswordForm, {
    actionUrl: d.actionUrl ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    targetUserId: d.targetUserId ?? "",
    showCurrent: d.showCurrent ?? "false",
    currentPasswordLabel: d.currentPasswordLabel ?? "",
    newPasswordLabel: d.newPasswordLabel ?? "",
    confirmNewPasswordLabel: d.confirmNewPasswordLabel ?? "",
    submitLabel: d.submitLabel ?? "",
    requiredMsg: d.requiredMsg ?? "",
    passwordTooShortMsg: d.passwordTooShortMsg ?? "",
    passwordMismatchMsg: d.passwordMismatchMsg ?? "",
    minLength: Number(d.passwordMinLength ?? 12),
    passwordHintMsg: d.passwordHintMsg ?? "",
    strengthLabels: JSON.parse(d.passwordStrengthLabels ?? "[]")
  })).mount(el);
});

document.querySelectorAll("[data-vue-pagination-bar]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(PaginationBar, {
    page:                parseInt(d.page ?? "1"),
    totalPages:          parseInt(d.totalPages ?? "1"),
    baseUrl:             d.baseUrl ?? "",
    labelPageOf:         d.labelPageOf ?? "Page {0} of {1}",
    labelShowingResults: d.labelShowingResults ?? "",
  })).mount(el);
});

document.querySelectorAll("[data-vue-holiday-form]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(HolidayForm, {
    municipalitiesJson:  d.municipalitiesJson  ?? "[]",
    labelName:           d.labelName           ?? "",
    labelDate:           d.labelDate           ?? "",
    labelType:           d.labelType           ?? "",
    labelTypeFixed:      d.labelTypeFixed      ?? "",
    labelTypeRecurring:  d.labelTypeRecurring  ?? "",
    labelTypeMovable:    d.labelTypeMovable    ?? "",
    labelEasterOffset:   d.labelEasterOffset   ?? "",
    labelEasterCarnival:       d.labelEasterCarnival       ?? "",
    labelEasterGoodFriday:     d.labelEasterGoodFriday     ?? "",
    labelEasterSunday:         d.labelEasterSunday         ?? "",
    labelEasterCorpusChristi:  d.labelEasterCorpusChristi  ?? "",
    hintEasterOffset:    d.hintEasterOffset    ?? "",
    labelIsRecurring:    d.labelIsRecurring    ?? "",
    hintIsRecurring:     d.hintIsRecurring     ?? "",
    labelMunicipalities: d.labelMunicipalities ?? "",
    hintMunicipalities:  d.hintMunicipalities  ?? "",
  })).mount(el);
});

const settingsFormRoot = document.querySelector("[data-vue-settings-form]");

if (settingsFormRoot) {
  const d = settingsFormRoot.dataset;
  useQuasar(createApp(SettingsForm, {
    initialMinCoverage:            parseInt(d.initialMinCoverage ?? "50"),
    initialAllowBroadcastFallback: d.initialAllowBroadcastFallback !== "false",
    initialMaxNotifications:       parseInt(d.initialMaxNotifications ?? "3"),
    initialBaseHourlyRate:    parseFloat(d.initialBaseHourlyRate ?? "10"),
    initialWeekendMultiplier: parseFloat(d.initialWeekendMultiplier ?? "1.2"),
    initialHolidayMultiplier: parseFloat(d.initialHolidayMultiplier ?? "1.5"),
    errorMinCoverage:         d.errorMinCoverage ?? "",
    errorMaxNotifications:    d.errorMaxNotifications ?? "",
    errorBaseHourlyRate:      d.errorBaseHourlyRate ?? "",
    errorWeekendMultiplier:   d.errorWeekendMultiplier ?? "",
    errorHolidayMultiplier:   d.errorHolidayMultiplier ?? "",
    labelMinCoverage:              d.labelMinCoverage ?? "",
    labelAllowBroadcastFallback:   d.labelAllowBroadcastFallback ?? "",
    labelMaxNotifications:         d.labelMaxNotifications ?? "",
    labelBaseHourlyRate:      d.labelBaseHourlyRate ?? "",
    labelWeekendMultiplier:   d.labelWeekendMultiplier ?? "",
    labelHolidayMultiplier:   d.labelHolidayMultiplier ?? "",
    hintMinCoverage:               d.hintMinCoverage ?? "",
    hintAllowBroadcastFallback:    d.hintAllowBroadcastFallback ?? "",
    hintMaxNotifications:          d.hintMaxNotifications ?? "",
    hintBaseHourlyRate:       d.hintBaseHourlyRate ?? "",
    hintWeekendMultiplier:    d.hintWeekendMultiplier ?? "",
    hintHolidayMultiplier:    d.hintHolidayMultiplier ?? "",
    labelSectionService:      d.labelSectionService ?? "",
    labelSectionPricing:      d.labelSectionPricing ?? "",
    labelSectionCommission:   d.labelSectionCommission ?? "",
    labelSectionPayment:      d.labelSectionPayment ?? "",
    initialCommissionPercent: parseFloat(d.initialCommissionPercent ?? "20"),
    initialVatPercent:        parseFloat(d.initialVatPercent ?? "23"),
    errorCommissionRate:      d.errorCommissionRate ?? "",
    errorVatRate:             d.errorVatRate ?? "",
    labelCommissionRate:      d.labelCommissionRate ?? "",
    labelVatRate:             d.labelVatRate ?? "",
    hintCommissionRate:       d.hintCommissionRate ?? "",
    hintVatRate:              d.hintVatRate ?? "",
    initialPeriodCreationLeadDays:     parseInt(d.initialPeriodCreationLeadDays ?? "7"),
    errorPeriodCreationLeadDays:       d.errorPeriodCreationLeadDays ?? "",
    labelPeriodCreationLeadDays:       d.labelPeriodCreationLeadDays ?? "",
    hintPeriodCreationLeadDays:        d.hintPeriodCreationLeadDays ?? "",
    initialPaymentDeadlineHours:        parseInt(d.initialPaymentDeadlineHours ?? "48"),
    errorPaymentDeadlineHours:          d.errorPaymentDeadlineHours ?? "",
    labelPaymentDeadlineHours:          d.labelPaymentDeadlineHours ?? "",
    hintPaymentDeadlineHours:           d.hintPaymentDeadlineHours ?? "",
    initialInterestLeadHoursBeforePayment: parseInt(d.initialInterestLeadHoursBeforePayment ?? "12"),
    errorInterestLeadHoursBeforePayment:   d.errorInterestLeadHoursBeforePayment ?? "",
    labelInterestLeadHoursBeforePayment:   d.labelInterestLeadHoursBeforePayment ?? "",
    hintInterestLeadHoursBeforePayment:    d.hintInterestLeadHoursBeforePayment ?? "",
    initialMinInterestWindowHours:      parseInt(d.initialMinInterestWindowHours ?? "12"),
    errorMinInterestWindowHours:        d.errorMinInterestWindowHours ?? "",
    labelMinInterestWindowHours:        d.labelMinInterestWindowHours ?? "",
    hintMinInterestWindowHours:         d.hintMinInterestWindowHours ?? "",
    initialMultibancoLeadHoursBeforePayment:      parseInt(d.initialMultibancoLeadHoursBeforePayment ?? "168"),
    errorMultibancoLeadHoursBeforePayment:        d.errorMultibancoLeadHoursBeforePayment ?? "",
    labelMultibancoLeadHoursBeforePayment:        d.labelMultibancoLeadHoursBeforePayment ?? "",
    hintMultibancoLeadHoursBeforePayment:         d.hintMultibancoLeadHoursBeforePayment ?? "",
    initialBankTransferLeadHoursBeforePayment:    parseInt(d.initialBankTransferLeadHoursBeforePayment ?? "96"),
    errorBankTransferLeadHoursBeforePayment:      d.errorBankTransferLeadHoursBeforePayment ?? "",
    labelBankTransferLeadHoursBeforePayment:      d.labelBankTransferLeadHoursBeforePayment ?? "",
    hintBankTransferLeadHoursBeforePayment:       d.hintBankTransferLeadHoursBeforePayment ?? "",
    initialCardEnabled:         d.initialCardEnabled !== "false",
    initialMbwayEnabled:        d.initialMbwayEnabled !== "false",
    initialBankTransferEnabled: d.initialBankTransferEnabled !== "false",
    initialMultibancoEnabled:   d.initialMultibancoEnabled === "true",
    errorPaymentMethods:        d.errorPaymentMethods ?? "",
    labelCardEnabled:           d.labelCardEnabled ?? "",
    labelMbWayEnabled:          d.labelMbwayEnabled ?? "",
    labelBankTransferEnabled:   d.labelBankTransferEnabled ?? "",
    labelMultibancoEnabled:     d.labelMultibancoEnabled ?? "",
    hintPaymentMethods:         d.hintPaymentMethods ?? "",
    labelSectionInvoicing:         d.labelSectionInvoicing ?? "",
    initialMoloniInvoicingEnabled: d.initialMoloniInvoicingEnabled === "true",
    initialMoloniIssueClosed:      d.initialMoloniIssueClosed === "true",
    labelMoloniInvoicingEnabled:   d.labelMoloniInvoicingEnabled ?? "",
    labelMoloniIssueClosed:        d.labelMoloniIssueClosed ?? "",
    hintMoloniInvoicing:           d.hintMoloniInvoicing ?? "",
    hintMoloniIssueClosed:         d.hintMoloniIssueClosed ?? "",
    initialCaregiverInvoiceGateEnabled: d.initialCaregiverInvoiceGateEnabled !== "false",
    labelCaregiverInvoiceGate:     d.labelCaregiverInvoiceGate ?? "",
    hintCaregiverInvoiceGate:      d.hintCaregiverInvoiceGate ?? "",
    initialCaregiverInvoiceReviewRequired: d.initialCaregiverInvoiceReviewRequired !== "false",
    labelCaregiverInvoiceReview:   d.labelCaregiverInvoiceReview ?? "",
    hintCaregiverInvoiceReview:    d.hintCaregiverInvoiceReview ?? "",
    initialAutoInvoiceEnabled:     d.initialAutoInvoiceEnabled === "true",
    labelAutoInvoice:              d.labelAutoInvoice ?? "",
    hintAutoInvoice:               d.hintAutoInvoice ?? "",
    initialClinicalAccessGraceDays:    parseInt(d.initialClinicalAccessGraceDays ?? "3"),
    errorClinicalAccessGraceDays:      d.errorClinicalAccessGraceDays ?? "",
    labelClinicalAccessGraceDays:      d.labelClinicalAccessGraceDays ?? "",
    hintClinicalAccessGraceDays:       d.hintClinicalAccessGraceDays ?? "",
    initialOneShotMaxSpanDays:         parseInt(d.initialOneShotMaxSpanDays ?? "30"),
    errorOneShotMaxSpanDays:           d.errorOneShotMaxSpanDays ?? "",
    labelOneShotMaxSpanDays:           d.labelOneShotMaxSpanDays ?? "",
    hintOneShotMaxSpanDays:            d.hintOneShotMaxSpanDays ?? "",
    initialPreferredCaregiverLookbackMonths: parseInt(d.initialPreferredCaregiverLookbackMonths ?? "24"),
    errorPreferredCaregiverLookbackMonths:   d.errorPreferredCaregiverLookbackMonths ?? "",
    labelPreferredCaregiverLookbackMonths:   d.labelPreferredCaregiverLookbackMonths ?? "",
    hintPreferredCaregiverLookbackMonths:    d.hintPreferredCaregiverLookbackMonths ?? "",
    labelSectionSecurity:              d.labelSectionSecurity ?? "",
    hintSecurityRestart:               d.hintSecurityRestart ?? "",
    initialMaxLoginAttempts:           parseInt(d.initialMaxLoginAttempts ?? "10"),
    initialLockoutWindowMinutes:       parseInt(d.initialLockoutWindowMinutes ?? "15"),
    initialRateLimitPermits:           parseInt(d.initialRateLimitPermits ?? "10"),
    initialRateLimitWindowSeconds:     parseInt(d.initialRateLimitWindowSeconds ?? "300"),
    errorMaxLoginAttempts:             d.errorMaxLoginAttempts ?? "",
    errorLockoutWindowMinutes:         d.errorLockoutWindowMinutes ?? "",
    errorRateLimitPermits:             d.errorRateLimitPermits ?? "",
    errorRateLimitWindowSeconds:       d.errorRateLimitWindowSeconds ?? "",
    labelMaxLoginAttempts:             d.labelMaxLoginAttempts ?? "",
    labelLockoutWindowMinutes:         d.labelLockoutWindowMinutes ?? "",
    labelRateLimitPermits:             d.labelRateLimitPermits ?? "",
    labelRateLimitWindowSeconds:       d.labelRateLimitWindowSeconds ?? "",
    hintMaxLoginAttempts:              d.hintMaxLoginAttempts ?? "",
    hintLockoutWindowMinutes:          d.hintLockoutWindowMinutes ?? "",
    hintRateLimitPermits:              d.hintRateLimitPermits ?? "",
    hintRateLimitWindowSeconds:        d.hintRateLimitWindowSeconds ?? "",
  })).mount(settingsFormRoot);
}

const createNextPaymentRoot = document.querySelector("[data-vue-create-next-payment-form]");
if (createNextPaymentRoot) {
  const d = createNextPaymentRoot.dataset;
  useQuasar(createApp(CreateNextPaymentForm, {
    initialWeeklyRate:     parseFloat(d.initialWeeklyRate ?? "0"),
    initialCaregiverFee:   parseFloat(d.initialCaregiverFee ?? "0"),
    labelWeeklyRate:       d.labelWeeklyRate ?? "",
    labelCaregiverFeeRate: d.labelCaregiverFeeRate ?? "",
    hintWeeklyRate:        d.hintWeeklyRate ?? "",
    hintCaregiverFeeRate:  d.hintCaregiverFeeRate ?? "",
    labelCustomerNotes:    d.labelCustomerNotes ?? "",
    labelCaregiverNotes:   d.labelCaregiverNotes ?? "",
    initialPayoutMode:     d.initialPayoutMode ?? "",
    payoutModeOptions:     d.payoutModeOptions ?? "[]",
    labelPayoutMode:       d.labelPayoutMode ?? "",
    hintPayoutMode:        d.hintPayoutMode ?? "",
  })).mount(createNextPaymentRoot);
}

const createBackofficeUserFormRoot = document.querySelector("[data-vue-create-backoffice-user-form]");
if (createBackofficeUserFormRoot) {
  const d = createBackofficeUserFormRoot.dataset;
  useQuasar(createApp(CreateBackofficeUserForm, {
    actionUrl:                  d.actionUrl                  ?? "",
    antiforgeryToken:           d.antiforgeryToken           ?? "",
    cancelUrl:                  d.cancelUrl                  ?? "",
    labelFirstName:             d.labelFirstName             ?? "",
    labelLastName:              d.labelLastName              ?? "",
    labelEmail:                 d.labelEmail                 ?? "",
    labelRole:                  d.labelRole                  ?? "",
    hintRole:                   d.hintRole                   ?? "",
    labelRoleBackofficeManager: d.labelRoleBackofficeManager ?? "",
    labelRoleAdministrator:     d.labelRoleAdministrator     ?? "",
    submitLabel:                d.submitLabel                ?? "",
    cancelLabel:                d.cancelLabel                ?? "",
    requiredMsg:                d.requiredMsg                ?? "",
    invalidEmailMsg:            d.invalidEmailMsg            ?? "",
    initialFirstName:           d.initialFirstName           ?? "",
    initialLastName:            d.initialLastName            ?? "",
    initialEmail:               d.initialEmail               ?? "",
    initialRole:                d.initialRole                ?? RoleNames.BackofficeManager,
    errors:                     d.errors                     ?? "{}",
  })).mount(createBackofficeUserFormRoot);
}

const createCaregiverFormRoot = document.querySelector("[data-vue-create-caregiver-form]");
if (createCaregiverFormRoot) {
  const d = createCaregiverFormRoot.dataset;
  useQuasar(createApp(CreateCaregiverForm, {
    actionUrl:            d.actionUrl            ?? "",
    antiforgeryToken:     d.antiforgeryToken     ?? "",
    cancelUrl:            d.cancelUrl            ?? "",
    labelFirstName:       d.labelFirstName       ?? "",
    labelLastName:        d.labelLastName        ?? "",
    labelEmail:           d.labelEmail           ?? "",
    labelPhone:           d.labelPhone           ?? "",
    labelGender:          d.labelGender          ?? "",
    labelBirthDate:       d.labelBirthDate       ?? "",
    labelVatNumber:       d.labelVatNumber       ?? "",
    labelPreVetted:       d.labelPreVetted       ?? "",
    hintPreVetted:        d.hintPreVetted        ?? "",
    labelFemale:          d.labelFemale          ?? "",
    labelMale:            d.labelMale            ?? "",
    labelOther:           d.labelOther           ?? "",
    birthDateInFutureMsg: d.birthDateInFutureMsg ?? "",
    submitLabel:          d.submitLabel          ?? "",
    cancelLabel:          d.cancelLabel          ?? "",
    requiredMsg:          d.requiredMsg          ?? "",
    invalidEmailMsg:      d.invalidEmailMsg      ?? "",
    initialFirstName:     d.initialFirstName     ?? "",
    initialLastName:      d.initialLastName      ?? "",
    initialEmail:         d.initialEmail         ?? "",
    initialPhone:         d.initialPhone         ?? "",
    initialGender:        d.initialGender        ?? "",
    initialBirthDate:     d.initialBirthDate     ?? "",
    initialVatNumber:     d.initialVatNumber     ?? "",
    initialPreVetted:     d.initialPreVetted     ?? "false",
    errors:               d.errors               ?? "{}",
  })).mount(createCaregiverFormRoot);
}

const createCustomerFormRoot = document.querySelector("[data-vue-create-customer-form]");
if (createCustomerFormRoot) {
  const d = createCustomerFormRoot.dataset;
  useQuasar(createApp(CreateCustomerForm, {
    actionUrl:             d.actionUrl             ?? "",
    antiforgeryToken:      d.antiforgeryToken      ?? "",
    municipalitiesJson:    d.municipalitiesJson    ?? "[]",
    cancelUrl:             d.cancelUrl             ?? "",
    labelFirstName:        d.labelFirstName        ?? "",
    labelLastName:         d.labelLastName         ?? "",
    labelEmail:            d.labelEmail            ?? "",
    labelPhone:            d.labelPhone            ?? "",
    labelGender:           d.labelGender           ?? "",
    labelBirthDate:        d.labelBirthDate        ?? "",
    labelVatNumber:        d.labelVatNumber        ?? "",
    labelAddressLine1:     d.labelAddressLine1     ?? "",
    labelAddressLine2:     d.labelAddressLine2     ?? "",
    labelPostalCode:       d.labelPostalCode       ?? "",
    invalidPostalCodeMsg:  d.invalidPostalCodeMsg  ?? "",
    labelDistrict:         d.labelDistrict         ?? "",
    labelMunicipality:     d.labelMunicipality     ?? "",
    labelFemale:           d.labelFemale           ?? "",
    labelMale:             d.labelMale             ?? "",
    labelOther:            d.labelOther            ?? "",
    birthDateInFutureMsg:  d.birthDateInFutureMsg  ?? "",
    submitLabel:           d.submitLabel           ?? "",
    cancelLabel:           d.cancelLabel           ?? "",
    requiredMsg:           d.requiredMsg           ?? "",
    invalidEmailMsg:       d.invalidEmailMsg       ?? "",
    regionsJson:           d.regionsJson           ?? "[]",
    initialFirstName:      d.initialFirstName      ?? "",
    initialLastName:       d.initialLastName       ?? "",
    initialEmail:          d.initialEmail          ?? "",
    initialPhone:          d.initialPhone          ?? "",
    initialGender:         d.initialGender         ?? "",
    initialBirthDate:      d.initialBirthDate      ?? "",
    initialVatNumber:      d.initialVatNumber      ?? "",
    initialAddressLine1:   d.initialAddressLine1   ?? "",
    initialAddressLine2:   d.initialAddressLine2   ?? "",
    initialPostalCode:     d.initialPostalCode     ?? "",
    initialDistrictId:     d.initialDistrictId     ?? "",
    initialMunicipalityId: d.initialMunicipalityId ?? "",
    errors:                d.errors                ?? "{}",
    labelLocate:           d.labelLocate           ?? "",
    labelMapHint:          d.labelMapHint          ?? "",
  })).mount(createCustomerFormRoot);
}

const districtFormRoot = document.querySelector("[data-vue-district-form]");
if (districtFormRoot) {
  const d = districtFormRoot.dataset;
  useQuasar(createApp(DistrictForm, {
    actionUrl:        d.actionUrl        ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    cancelUrl:        d.cancelUrl        ?? "",
    initialName:      d.initialName      ?? "",
    initialCode:      d.initialCode      ?? "",
    initialIsActive:  d.initialIsActive  ?? "true",
    showStatus:       d.showStatus       ?? "false",
    labelName:        d.labelName        ?? "",
    labelCode:        d.labelCode        ?? "",
    labelActive:      d.labelActive      ?? "",
    labelSave:        d.labelSave        ?? "",
    labelCancel:      d.labelCancel      ?? "",
    errors:           d.errors ?? "{}",
    requiredMsg:      d.requiredMsg      ?? "",
  })).mount(districtFormRoot);
}

const municipalityFormRoot = document.querySelector("[data-vue-municipality-form]");
if (municipalityFormRoot) {
  const d = municipalityFormRoot.dataset;
  useQuasar(createApp(MunicipalityForm, {
    actionUrl:         d.actionUrl         ?? "",
    antiforgeryToken:  d.antiforgeryToken  ?? "",
    cancelUrl:         d.cancelUrl         ?? "",
    initialName:       d.initialName       ?? "",
    initialDistrictId: d.initialDistrictId ?? "",
    initialCode:       d.initialCode       ?? "",
    initialLatitude:   d.initialLatitude   ?? "",
    initialLongitude:  d.initialLongitude  ?? "",
    initialIsActive:   d.initialIsActive   ?? "true",
    showStatus:        d.showStatus        ?? "false",
    districtsJson:     d.districtsJson     ?? "[]",
    labelName:         d.labelName         ?? "",
    labelDistrict:     d.labelDistrict     ?? "",
    labelCode:         d.labelCode         ?? "",
    labelLatitude:     d.labelLatitude     ?? "",
    labelLongitude:    d.labelLongitude    ?? "",
    labelActive:       d.labelActive       ?? "",
    labelSave:         d.labelSave         ?? "",
    labelCancel:       d.labelCancel       ?? "",
    errors:            d.errors ?? "{}",
    requiredMsg:       d.requiredMsg       ?? "",
  })).mount(municipalityFormRoot);
}

const localityFormRoot = document.querySelector("[data-vue-locality-form]");
if (localityFormRoot) {
  const d = localityFormRoot.dataset;
  useQuasar(createApp(LocalityForm, {
    actionUrl:             d.actionUrl             ?? "",
    antiforgeryToken:      d.antiforgeryToken      ?? "",
    cancelUrl:             d.cancelUrl             ?? "",
    initialName:           d.initialName           ?? "",
    initialMunicipalityId: d.initialMunicipalityId ?? "",
    initialDistrictId:     d.initialDistrictId     ?? "",
    initialPostalCode:     d.initialPostalCode     ?? "",
    initialCode:           d.initialCode           ?? "",
    initialLatitude:       d.initialLatitude       ?? "",
    initialLongitude:      d.initialLongitude      ?? "",
    initialIsActive:       d.initialIsActive       ?? "true",
    showStatus:            d.showStatus            ?? "false",
    districtsJson:         d.districtsJson         ?? "[]",
    municipalitiesJson:    d.municipalitiesJson     ?? "[]",
    labelName:             d.labelName             ?? "",
    labelDistrict:         d.labelDistrict         ?? "",
    labelMunicipality:     d.labelMunicipality     ?? "",
    labelPostalCode:       d.labelPostalCode       ?? "",
    labelCode:             d.labelCode             ?? "",
    labelLatitude:         d.labelLatitude         ?? "",
    labelLongitude:        d.labelLongitude        ?? "",
    labelActive:           d.labelActive           ?? "",
    labelSave:             d.labelSave             ?? "",
    labelCancel:           d.labelCancel           ?? "",
    errors:                d.errors ?? "{}",
    requiredMsg:           d.requiredMsg           ?? "",
  })).mount(localityFormRoot);
}

const districtFilterFormRoot = document.querySelector("[data-vue-district-filter-form]");
if (districtFilterFormRoot) {
  const d = districtFilterFormRoot.dataset;
  useQuasar(createApp(DistrictFilterForm, {
    actionUrl:     d.actionUrl     ?? "",
    initialSearch: d.initialSearch ?? "",
    labelSearch:   d.labelSearch   ?? "",
    labelFilter:   d.labelFilter   ?? "",
  })).mount(districtFilterFormRoot);
}

const municipalityFilterFormRoot = document.querySelector("[data-vue-municipality-filter-form]");
if (municipalityFilterFormRoot) {
  const d = municipalityFilterFormRoot.dataset;
  useQuasar(createApp(MunicipalityFilterForm, {
    actionUrl:         d.actionUrl         ?? "",
    initialSearch:     d.initialSearch     ?? "",
    initialDistrictId: d.initialDistrictId ?? "",
    districtsJson:     d.districtsJson     ?? "[]",
    labelSearch:       d.labelSearch       ?? "",
    labelDistrict:     d.labelDistrict     ?? "",
    labelFilter:       d.labelFilter       ?? "",
  })).mount(municipalityFilterFormRoot);
}

const legalCompareFormRoot = document.querySelector("[data-vue-legal-compare-form]");
if (legalCompareFormRoot) {
  const d = legalCompareFormRoot.dataset;
  useQuasar(createApp(LegalCompareForm, {
    actionUrl:     d.actionUrl     ?? "",
    versionsJson:  d.versionsJson  ?? "[]",
    initialFromId: d.initialFromId ?? "",
    initialToId:   d.initialToId   ?? "",
    labelFrom:     d.labelFrom     ?? "",
    labelTo:       d.labelTo       ?? "",
  })).mount(legalCompareFormRoot);
}

const localityFilterFormRoot = document.querySelector("[data-vue-locality-filter-form]");
if (localityFilterFormRoot) {
  const d = localityFilterFormRoot.dataset;
  useQuasar(createApp(LocalityFilterForm, {
    actionUrl:             d.actionUrl             ?? "",
    initialSearch:         d.initialSearch         ?? "",
    initialDistrictId:     d.initialDistrictId     ?? "",
    initialMunicipalityId: d.initialMunicipalityId ?? "",
    districtsJson:         d.districtsJson         ?? "[]",
    municipalitiesJson:    d.municipalitiesJson    ?? "[]",
    labelSearch:           d.labelSearch           ?? "",
    labelDistrict:         d.labelDistrict         ?? "",
    labelMunicipality:     d.labelMunicipality     ?? "",
    labelFilter:           d.labelFilter           ?? "",
  })).mount(localityFilterFormRoot);
}

const holidayFilterFormRoot = document.querySelector("[data-vue-holiday-filter-form]");
if (holidayFilterFormRoot) {
  const d = holidayFilterFormRoot.dataset;
  useQuasar(createApp(HolidayFilterForm, {
    actionUrl:       d.actionUrl ?? "",
    initialSearch:   d.initialSearch ?? "",
    initialType:     d.initialType ?? "",
    labelSearch:     d.labelSearch ?? "",
    labelType:       d.labelType ?? "",
    labelFilter:     d.labelFilter ?? "",
    labelNationwide: d.labelNationwide ?? "",
    labelMunicipal:  d.labelMunicipal ?? "",
  })).mount(holidayFilterFormRoot);
}

const rescindInterestFormRoot = document.querySelector("[data-vue-rescind-interest-form]");
if (rescindInterestFormRoot) {
  const d = rescindInterestFormRoot.dataset;
  useQuasar(createApp(RescindInterestForm, {
    actionUrl:                d.actionUrl                ?? "",
    antiforgeryToken:         d.antiforgeryToken         ?? "",
    cancelUrl:                d.cancelUrl                ?? "",
    interestId:               d.interestId               ?? "",
    requestNumber:            d.requestNumber            ?? "",
    customerName:             d.customerName             ?? "",
    reasons:                  d.reasons                  ?? "[]",
    initialSelectedReason:    d.initialSelectedReason    ?? "",
    initialAdditionalDetails: d.initialAdditionalDetails ?? "",
    additionalDetailsLabel:   d.additionalDetailsLabel   ?? "",
    submitLabel:              d.submitLabel              ?? "",
    cancelLabel:              d.cancelLabel              ?? "",
    requiredMsg:              d.requiredMsg              ?? "",
    errors:                   d.errors                   ?? "{}",
  })).mount(rescindInterestFormRoot);
}

const withdrawRequestFormRoot = document.querySelector("[data-vue-withdraw-request-form]");
if (withdrawRequestFormRoot) {
  const d = withdrawRequestFormRoot.dataset;
  useQuasar(createApp(WithdrawRequestForm, {
    actionUrl:                d.actionUrl                  ?? "",
    antiforgeryToken:         d.antiforgeryToken           ?? "",
    cancelUrl:                d.cancelUrl                  ?? "",
    requestId:                d.requestId                  ?? "",
    requestNumber:            d.requestNumber              ?? "",
    reasons:                  d.reasons                    ?? "[]",
    initialSelectedReason:    d.initialSelectedReason      ?? "",
    initialAdditionalDetails: d.initialAdditionalDetails   ?? "",
    additionalDetailsLabel:   d.additionalDetailsLabel     ?? "",
    submitLabel:              d.submitLabel                ?? "",
    cancelLabel:              d.cancelLabel                ?? "",
    requiredMsg:              d.requiredMsg                ?? "",
    errors:                   d.errors                     ?? "{}",
  })).mount(withdrawRequestFormRoot);
}

// ── Upcoming schedule timeline ─────────────────────────────────

document.querySelectorAll("[data-vue-upcoming-schedule-timeline]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(UpcomingScheduleTimeline, {
    occurrences:    d.occurrences    ?? "[]",
    emptyMessage:   d.emptyMessage   ?? "",
    viewAllUrl:     d.viewAllUrl     ?? "#",
    viewAllLabel:   d.viewAllLabel   ?? "Ver todos",
    viewVisitLabel: d.viewVisitLabel ?? "",
  })).mount(el);
});

// ── Customer my-profile form ───────────────────────────────────

const customerMyProfileRoot = document.querySelector("[data-vue-customer-my-profile-form]");
if (customerMyProfileRoot) {
  const d = customerMyProfileRoot.dataset;
  useQuasar(createApp(CustomerMyProfileForm, {
    actionUrl:         d.actionUrl         ?? "",
    antiforgeryToken:  d.antiforgeryToken  ?? "",
    labelFirstName:    d.labelFirstName    ?? "",
    labelLastName:     d.labelLastName     ?? "",
    labelGender:       d.labelGender       ?? "",
    labelBirthDate:    d.labelBirthDate    ?? "",
    birthDateInFutureMsg: d.birthDateInFutureMsg ?? "",
    labelVatNumber:    d.labelVatNumber    ?? "",
    labelAddressLine1: d.labelAddressLine1 ?? "",
    labelAddressLine2: d.labelAddressLine2 ?? "",
    labelPostalCode:   d.labelPostalCode   ?? "",
    invalidPostalCodeMsg: d.invalidPostalCodeMsg ?? "",
    labelDistrict:     d.labelDistrict     ?? "",
    labelMunicipality: d.labelMunicipality ?? "",
    submitLabel:       d.submitLabel       ?? "",
    requiredMsg:       d.requiredMsg       ?? "",
    genderOptions:     d.genderOptions     ?? "[]",
    districts:         d.districts         ?? "[]",
    municipalities:    d.municipalities    ?? "[]",
    model:             d.model             ?? "{}",
    errors:            d.errors            ?? "{}",
    labelLocate:       d.labelLocate       ?? "",
    labelMapHint:      d.labelMapHint      ?? "",
  })).mount(customerMyProfileRoot);
}

// ── Care recipient form ────────────────────────────────────────

const careRecipientFormRoot = document.querySelector("[data-vue-care-recipient-form]");
if (careRecipientFormRoot) {
  const d = careRecipientFormRoot.dataset;
  useQuasar(createApp(CareRecipientForm, {
    actionUrl:             d.actionUrl             ?? "",
    antiforgeryToken:      d.antiforgeryToken      ?? "",
    cancelUrl:             d.cancelUrl             ?? "",
    labelFirstName:        d.labelFirstName        ?? "",
    labelLastName:         d.labelLastName         ?? "",
    labelBirthDate:        d.labelBirthDate        ?? "",
    labelGender:           d.labelGender           ?? "",
    labelRelationship:     d.labelRelationship     ?? "",
    labelMobility:         d.labelMobility         ?? "",
    labelCognitive:        d.labelCognitive        ?? "",
    labelMedicalNotes:     d.labelMedicalNotes     ?? "",
    labelMedications:      d.labelMedications      ?? "",
    labelAllergies:        d.labelAllergies        ?? "",
    labelEmergencyContact: d.labelEmergencyContact ?? "",
    labelAdditionalNotes:  d.labelAdditionalNotes  ?? "",
    labelNeedsTitle:       d.labelNeedsTitle       ?? "",
    labelNeedsHint:        d.labelNeedsHint        ?? "",
    submitLabel:           d.submitLabel           ?? "",
    cancelLabel:           d.cancelLabel           ?? "",
    requiredMsg:           d.requiredMsg           ?? "",
    genderOptions:         d.genderOptions         ?? "[]",
    relationshipOptions:   d.relationshipOptions   ?? "[]",
    mobilityOptions:       d.mobilityOptions       ?? "[]",
    cognitiveOptions:      d.cognitiveOptions      ?? "[]",
    allServices:           d.allServices           ?? "[]",
    model:                 d.model                 ?? "{}",
    errors:                d.errors                ?? "{}",
    isSelf:                d.isSelf === "true",
    profileUrl:            d.profileUrl            ?? "",
    labelSelfIdentityManaged: d.labelSelfIdentityManaged ?? "",
    labelMyProfile:        d.labelMyProfile        ?? "",
  })).mount(careRecipientFormRoot);
}

// ── Global styled confirmation dialog ──────────────────────────

// The dialog registers its `pc:confirm` listener in setup, which now runs only
// once its chunk has arrived. The submit interceptor at the foot of this file
// calls preventDefault() the moment the page is interactive, so a click landing
// inside that window would be swallowed with nothing on screen to show for it —
// hence a promise the dispatch can wait on. `.mount()` runs setup synchronously,
// so the listener exists by the time this resolves.
const confirmDialogRoot = document.getElementById("confirm-dialog-root");
const confirmDialogReady = confirmDialogRoot
  ? import("@/components/ConfirmActionDialog.vue").then(m => {
      useQuasar(createApp(m.default)).mount(confirmDialogRoot);
    })
  : Promise.resolve();

// ── Message reply forms ────────────────────────────────────────

const messageReplySelectors = [
  "[data-vue-caregiver-message-reply-form]",
  "[data-vue-customer-message-reply-form]",
  "[data-vue-bo-message-reply-form]",
];
messageReplySelectors.forEach(selector => {
  const el = document.querySelector(selector);
  if (el) {
    const d = el.dataset;
    useQuasar(createApp(MessageReplyForm, {
      actionUrl:        d.actionUrl        ?? "",
      antiforgeryToken: d.antiforgeryToken ?? "",
      threadId:         d.threadId         ?? "",
      contentLabel:     d.contentLabel     ?? "",
      submitLabel:      d.submitLabel      ?? "",
      requiredMsg:      d.requiredMsg      ?? "",
      errors:           d.errors           ?? "{}",
    })).mount(el);
  }
});

// ── New thread forms ───────────────────────────────────────────

const newThreadSelectors = [
  "[data-vue-caregiver-new-thread-form]",
  "[data-vue-customer-new-thread-form]",
];
newThreadSelectors.forEach(selector => {
  const el = document.querySelector(selector);
  if (el) {
    const d = el.dataset;
    useQuasar(createApp(NewThreadForm, {
      actionUrl:        d.actionUrl        ?? "",
      antiforgeryToken: d.antiforgeryToken ?? "",
      contractsJson:    d.contractsJson    ?? "[]",
      labelRecipient:   d.labelRecipient   ?? "",
      labelBackoffice:  d.labelBackoffice  ?? "",
      labelContact:     d.labelContact     ?? "",
      labelContract:    d.labelContract    ?? "",
      labelSubject:     d.labelSubject     ?? "",
      labelContent:     d.labelContent     ?? "",
      submitLabel:      d.submitLabel      ?? "",
      requiredMsg:      d.requiredMsg      ?? "",
    })).mount(el);
  }
});

// ── Edit caregiver form ────────────────────────────────────────

const editCaregiverFormRoot = document.querySelector("[data-vue-edit-caregiver-form]");
if (editCaregiverFormRoot) {
  const d = editCaregiverFormRoot.dataset;
  useQuasar(createApp(EditCaregiverForm, {
    actionUrl:                 d.actionUrl                 ?? "",
    antiforgeryToken:          d.antiforgeryToken          ?? "",
    cancelUrl:                 d.cancelUrl                 ?? "",
    labelFirstName:            d.labelFirstName            ?? "",
    labelLastName:             d.labelLastName             ?? "",
    labelGender:               d.labelGender               ?? "",
    labelBirthDate:            d.labelBirthDate            ?? "",
    labelVatNumber:            d.labelVatNumber            ?? "",
    labelShortBio:             d.labelShortBio             ?? "",
    labelFullBio:              d.labelFullBio              ?? "",
    labelRegistrationStatus:   d.labelRegistrationStatus   ?? "",
    labelActive:               d.labelActive               ?? "",
    sectionPersonalInfo:       d.sectionPersonalInfo       ?? "",
    sectionBio:                d.sectionBio                ?? "",
    sectionStatus:             d.sectionStatus             ?? "",
    submitLabel:               d.submitLabel               ?? "",
    cancelLabel:               d.cancelLabel               ?? "",
    requiredMsg:               d.requiredMsg               ?? "",
    labelDefaultPayoutMode:    d.labelDefaultPayoutMode    ?? "",
    genderOptions:             d.genderOptions             ?? "[]",
    registrationStatusOptions: d.registrationStatusOptions ?? "[]",
    payoutModeOptions:         d.payoutModeOptions         ?? "[]",
    model:                     d.model                     ?? "{}",
    errors:                    d.errors                    ?? "{}",
  })).mount(editCaregiverFormRoot);
}

// ── Edit customer form ─────────────────────────────────────────

const editCustomerFormRoot = document.querySelector("[data-vue-edit-customer-form]");
if (editCustomerFormRoot) {
  const d = editCustomerFormRoot.dataset;
  useQuasar(createApp(EditCustomerForm, {
    actionUrl:       d.actionUrl       ?? "",
    antiforgeryToken:d.antiforgeryToken ?? "",
    cancelUrl:       d.cancelUrl       ?? "",
    labelFirstName:  d.labelFirstName  ?? "",
    labelLastName:   d.labelLastName   ?? "",
    labelGender:     d.labelGender     ?? "",
    labelBirthDate:  d.labelBirthDate  ?? "",
    labelVatNumber:  d.labelVatNumber  ?? "",
    labelPhone:      d.labelPhone      ?? "",
    labelActive:     d.labelActive     ?? "",
    sectionPersonalInfo: d.sectionPersonalInfo ?? "",
    sectionStatus:   d.sectionStatus   ?? "",
    submitLabel:     d.submitLabel     ?? "",
    cancelLabel:     d.cancelLabel     ?? "",
    requiredMsg:     d.requiredMsg     ?? "",
    genderOptions:   d.genderOptions   ?? "[]",
    model:           d.model           ?? "{}",
    errors:          d.errors          ?? "{}",
  })).mount(editCustomerFormRoot);
}

// ── CompleteProfile gender island ──────────────────────────────

const caregiverGenderRoot = document.querySelector("[data-vue-caregiver-gender-select]");
if (caregiverGenderRoot) {
  const d = caregiverGenderRoot.dataset;
  useQuasar(createApp(CaregiverGenderSelect, {
    label:               d.label               ?? "",
    hint:                d.hint                ?? "",
    options:             d.options             ?? "[]",
    initial:             d.initial             ?? "",
  })).mount(caregiverGenderRoot);
}

// ── CompleteProfile text island ────────────────────────────────

const completeProfileTextRoot = document.querySelector("[data-vue-complete-profile-text-form]");
if (completeProfileTextRoot) {
  const d = completeProfileTextRoot.dataset;
  useQuasar(createApp(CompleteProfileTextForm, {
    labelShortBio:       d.labelShortBio       ?? "",
    hintShortBio:        d.hintShortBio        ?? "",
    labelFullBio:        d.labelFullBio        ?? "",
    labelSlot:           d.labelSlot           ?? "",
    slots:               d.slots               ?? "[]",
    initialShortBio:     d.initialShortBio     ?? "",
    initialFullBio:      d.initialFullBio      ?? "",
    initialSlotId:       d.initialSlotId       ?? "",
  })).mount(completeProfileTextRoot);
}

// ── Create request form ────────────────────────────────────────

const createRequestFormRoot = document.querySelector("[data-vue-create-request-form]");
if (createRequestFormRoot) {
  const d = createRequestFormRoot.dataset;
  useQuasar(createApp(CreateRequestForm, {
    actionUrl:           d.actionUrl           ?? "",
    antiforgeryToken:    d.antiforgeryToken    ?? "",
    paymentDeadlineHours: d.paymentDeadlineHours ?? "48",
    oneShotMaxSpanDays:  d.oneShotMaxSpanDays  ?? "30",
    cancelUrl:           d.cancelUrl           ?? "",
    needsUrl:            d.needsUrl            ?? "",
    labelDescription:    d.labelDescription    ?? "",
    labelSelectPersons:  d.labelSelectPersons  ?? "",
    labelSelectServices: d.labelSelectServices ?? "",
    labelRequestType:    d.labelRequestType    ?? "",
    labelRecurrent:      d.labelRecurrent      ?? "",
    labelOneShot:        d.labelOneShot        ?? "",
    labelPreferredStartDate: d.labelPreferredStartDate ?? "",
    labelSchedule:       d.labelSchedule       ?? "",
    labelDayOfWeek:      d.labelDayOfWeek      ?? "",
    labelSpecificDate:   d.labelSpecificDate   ?? "",
    labelStartTime:      d.labelStartTime      ?? "",
    labelEndTime:        d.labelEndTime        ?? "",
    labelAddSlot:        d.labelAddSlot        ?? "",
    submitLabel:         d.submitLabel         ?? "",
    cancelLabel:         d.cancelLabel         ?? "",
    requiredMsg:         d.requiredMsg         ?? "",
    scheduleOverlapMsg:  d.scheduleOverlapMsg  ?? "",
    noServicesMsg:       d.noServicesMsg       ?? "",
    noPersonsMsg:        d.noPersonsMsg        ?? "",
    autoSuggestHint:     d.autoSuggestHint     ?? "",
    personsJson:         d.personsJson         ?? "[]",
    addressesJson:       d.addressesJson       ?? "[]",
    labelServiceAddress: d.labelServiceAddress  ?? "",
    selectServiceAddressMsg: d.selectServiceAddressMsg ?? "",
    servicesJson:        d.servicesJson        ?? "[]",
    dayNamesJson:        d.dayNamesJson        ?? "[]",
    modelJson:           d.modelJson           ?? "{}",
    eligibleCaregiversJson: d.eligibleCaregiversJson ?? "[]",
    labelPreferredCaregiver: d.labelPreferredCaregiver ?? "",
    preferredCaregiverHint:  d.preferredCaregiverHint  ?? "",
  })).mount(createRequestFormRoot);
}

// ── Create slot form ──────────────────────────────────────────

const createSlotFormRoot = document.querySelector("[data-vue-create-slot-form]");
if (createSlotFormRoot) {
  const d = createSlotFormRoot.dataset;
  useQuasar(createApp(CreateSlotForm, {
    actionUrl:        d.actionUrl        ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    cancelUrl:        d.cancelUrl        ?? "",
    labelStart:       d.labelStart       ?? "",
    labelEnd:         d.labelEnd         ?? "",
    labelNotes:       d.labelNotes       ?? "",
    submitLabel:      d.submitLabel      ?? "",
    cancelLabel:      d.cancelLabel      ?? "",
    requiredMsg:      d.requiredMsg      ?? "",
    endAfterStartMsg: d.endAfterStartMsg ?? "",
    initialStartDate: d.initialStartDate ?? "",
    initialStartTime: d.initialStartTime ?? "",
    initialEndDate:   d.initialEndDate   ?? "",
    initialEndTime:   d.initialEndTime   ?? "",
    minDate:          d.minDate          ?? "",
  })).mount(createSlotFormRoot);
}

// ── Availability form ──────────────────────────────────────────

const availabilityBatchFormRoot = document.querySelector("[data-vue-availability-batch-form]");
if (availabilityBatchFormRoot) {
  const d = availabilityBatchFormRoot.dataset;
  useQuasar(createApp(AvailabilityBatchForm, {
    actionUrl:              d.actionUrl             ?? "",
    antiforgeryToken:       d.antiforgeryToken      ?? "",
    availabilityUrl:        d.availabilityUrl       ?? "",
    cancelUrl:              d.cancelUrl             ?? "",
    labelSlotType:          d.labelSlotType         ?? "",
    labelRecurring:         d.labelRecurring        ?? "",
    labelOneOff:            d.labelOneOff           ?? "",
    labelDayOfWeek:         d.labelDayOfWeek        ?? "",
    labelDayDate:           d.labelDayDate          ?? "",
    labelSpecificDate:      d.labelSpecificDate     ?? "",
    labelStartTime:         d.labelStartTime        ?? "",
    labelEndTime:           d.labelEndTime          ?? "",
    labelValidFrom:         d.labelValidFrom        ?? "",
    labelValidUntil:        d.labelValidUntil       ?? "",
    labelIndefinite:        d.labelIndefinite       ?? "",
    labelAddSlot:           d.labelAddSlot          ?? "",
    labelSaveAll:           d.labelSaveAll          ?? "",
    labelRemove:            d.labelRemove           ?? "",
    labelCancel:            d.labelCancel           ?? "",
    labelChangeValidity:    d.labelChangeValidity   ?? "",
    labelValidityHint:      d.labelValidityHint     ?? "",
    labelSlotEndAfterStart: d.labelSlotEndAfterStart ?? "",
    labelValidUntilBeforeValidFrom: d.labelValidUntilBeforeValidFrom ?? "",
    requiredMsg:            d.requiredMsg           ?? "",
    slotDateInPastMsg:      d.slotDateInPastMsg     ?? "",
    dayNamesJson:           d.dayNamesJson          ?? "[]",
  })).mount(availabilityBatchFormRoot);
}

// ── Invoice upload form (caregiver + backoffice, shared component) ─────────────

document.querySelectorAll("[data-vue-invoice-upload-form]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(InvoiceUploadForm, {
    actionUrl:        d.actionUrl        ?? "",
    paymentId:        d.paymentId        ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    cancelUrl:        d.cancelUrl        ?? "",
    existingDocName:  d.existingDocName  ?? "",
    existingDocUrl:   d.existingDocUrl   ?? "",
    labelFile:        d.labelFile        ?? "",
    labelHint:        d.labelHint        ?? "",
    labelSubmit:      d.labelSubmit      ?? "",
    labelCancel:      d.labelCancel      ?? "",
    labelExistingDoc: d.labelExistingDoc ?? "",
    labelDownload:    d.labelDownload    ?? "",
    requiredMsg:      d.requiredMsg      ?? "",
    showAttestation:        d.showAttestation === "true",
    labelCoverageContract:  d.labelCoverageContract  ?? "",
    labelCoverageHint:      d.labelCoverageHint      ?? "",
    labelAttestation:       d.labelAttestation       ?? "",
    attestationRequiredMsg: d.attestationRequiredMsg ?? "",
    labelError:             d.labelError             ?? "",
    initialCoversWholeContract: d.initialCoversWholeContract === "true",
  })).mount(el);
});

// ── Caregiver dashboard filter forms ──────────────────────────

const caregiverContractsFilterRoot = document.querySelector("[data-vue-caregiver-contracts-filter-form]");
if (caregiverContractsFilterRoot) {
  const d = caregiverContractsFilterRoot.dataset;
  useQuasar(createApp(CaregiverContractsFilterForm, {
    actionUrl:         d.actionUrl         ?? "",
    initialStatus:     d.initialStatus     ?? "",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelStatus:       d.labelStatus       ?? "",
    labelFilter:       d.labelFilter       ?? "",
  })).mount(caregiverContractsFilterRoot);
}

const customerRequestFilterRoot = document.querySelector("[data-vue-customer-request-filter-form]");
if (customerRequestFilterRoot) {
  const d = customerRequestFilterRoot.dataset;
  useQuasar(createApp(CustomerRequestFilterForm, {
    actionUrl:         d.actionUrl         ?? "",
    initialStatus:     d.initialStatus     ?? "",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelStatus:       d.labelStatus       ?? "",
    labelFilter:       d.labelFilter       ?? "",
  })).mount(customerRequestFilterRoot);
}

const customerPaymentStatusFilterRoot = document.querySelector("[data-vue-customer-payment-status-filter-form]");
if (customerPaymentStatusFilterRoot) {
  const d = customerPaymentStatusFilterRoot.dataset;
  useQuasar(createApp(CustomerPaymentStatusFilterForm, {
    actionUrl:         d.actionUrl         ?? "",
    initialStatus:     d.initialStatus     ?? "",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelStatus:       d.labelStatus       ?? "",
    labelFilter:       d.labelFilter       ?? "",
  })).mount(customerPaymentStatusFilterRoot);
}

const caregiverVisitsFilterRoot = document.querySelector("[data-vue-caregiver-visits-filter-form]");
if (caregiverVisitsFilterRoot) {
  const d = caregiverVisitsFilterRoot.dataset;
  useQuasar(createApp(CaregiverVisitsFilterForm, {
    actionUrl:         d.actionUrl         ?? "",
    initialStatus:     d.initialStatus     ?? "",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelStatus:       d.labelStatus       ?? "",
    labelFilter:       d.labelFilter       ?? "",
  })).mount(caregiverVisitsFilterRoot);
}

const antiforgeryToken = () =>
  document.querySelector("input[name=__RequestVerificationToken]")?.value ?? "";

const visitBatchBarRoot = document.querySelector("[data-vue-visit-batch-bar]");
// The bar owns the row checkboxes, so it is only mounted where there is a table to own: with no
// row island the bar could never reach a non-zero selection and would stay hidden forever.
const visitRowsRoot = document.querySelector("[data-vue-visit-rows]");
if (visitBatchBarRoot && visitRowsRoot) {
  const d = visitBatchBarRoot.dataset;
  useQuasar(createApp(VisitBatchBar, {
    root:           visitRowsRoot,
    batchActionUrl: d.batchActionUrl ?? "",
    returnUrl:      d.returnUrl      ?? "",
    antiforgery:    antiforgeryToken(),
    labelValidate:  d.labelValidate  ?? "",
    labelInvoice:   d.labelInvoice   ?? "",
    labelSelected:  d.labelSelected  ?? "",
  })).mount(visitBatchBarRoot);
}

const caregiverInvoiceDrawerRoot = document.querySelector("[data-vue-caregiver-invoice-drawer]");
if (caregiverInvoiceDrawerRoot) {
  const d = caregiverInvoiceDrawerRoot.dataset;
  useQuasar(createApp(CaregiverInvoiceDrawer, {
    billableUrl:              d.billableUrl              ?? "",
    submitUrl:                d.submitUrl                ?? "",
    returnUrl:                d.returnUrl                ?? "",
    antiforgery:              antiforgeryToken(),
    reviewRequired:           d.reviewRequired === "1",
    labelTitle:               d.labelTitle               ?? "",
    labelThisPeriod:          d.labelThisPeriod          ?? "",
    labelValidatedVisits:     d.labelValidatedVisits     ?? "",
    labelWholeContract:       d.labelWholeContract       ?? "",
    labelPeriodsToDate:       d.labelPeriodsToDate       ?? "",
    oneShotContractIds:       JSON.parse(d.oneShotContractIds ?? "[]"),
    labelDone:                d.labelDone                ?? "",
    labelPending:             d.labelPending             ?? "",
    labelAhead:               d.labelAhead               ?? "",
    labelOnlyValidated:       d.labelOnlyValidated       ?? "",
    labelNoBillable:          d.labelNoBillable          ?? "",
    labelNoneForCoverage:     d.labelNoneForCoverage     ?? "",
    labelTotal:               d.labelTotal               ?? "",
    labelFile:                d.labelFile                ?? "",
    labelDeclaration:         d.labelDeclaration         ?? "",
    labelVisibleAfterReview:  d.labelVisibleAfterReview  ?? "",
    labelVisibleImmediately:  d.labelVisibleImmediately  ?? "",
    labelCancel:              d.labelCancel              ?? "",
    labelSubmit:              d.labelSubmit              ?? "",
    labelError:               d.labelError               ?? "",
  })).mount(caregiverInvoiceDrawerRoot);
}

const caregiverRequestsFilterRoot = document.querySelector("[data-vue-caregiver-requests-filter-form]");
if (caregiverRequestsFilterRoot) {
  const d = caregiverRequestsFilterRoot.dataset;
  useQuasar(createApp(CaregiverRequestsFilterForm, {
    actionUrl:         d.actionUrl         ?? "",
    initialStatus:     d.initialStatus     ?? "",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelStatus:       d.labelStatus       ?? "",
    labelFilter:       d.labelFilter       ?? "",
  })).mount(caregiverRequestsFilterRoot);
}

const caregiverPaymentStatusFilterRoot = document.querySelector("[data-vue-caregiver-payment-status-filter-form]");
if (caregiverPaymentStatusFilterRoot) {
  const d = caregiverPaymentStatusFilterRoot.dataset;
  useQuasar(createApp(CaregiverPaymentStatusFilterForm, {
    actionUrl:         d.actionUrl         ?? "",
    initialStatus:     d.initialStatus     ?? "",
    statusOptionsJson: d.statusOptionsJson ?? "[]",
    labelStatus:       d.labelStatus       ?? "",
    labelFilter:       d.labelFilter       ?? "",
  })).mount(caregiverPaymentStatusFilterRoot);
}

const caregiverAvailabilityFilterRoot = document.querySelector("[data-vue-caregiver-availability-filter-form]");
if (caregiverAvailabilityFilterRoot) {
  const d = caregiverAvailabilityFilterRoot.dataset;
  useQuasar(createApp(CaregiverAvailabilityFilterForm, {
    actionUrl:       d.actionUrl       ?? "",
    initialSlotType: d.initialSlotType ?? "",
    labelSlotType:   d.labelSlotType   ?? "",
    labelRecurring:  d.labelRecurring  ?? "",
    labelOneOff:     d.labelOneOff     ?? "",
    labelFilter:     d.labelFilter     ?? "",
  })).mount(caregiverAvailabilityFilterRoot);
}

const photoUploadInputRoot = document.querySelector("[data-vue-photo-upload-input]");
if (photoUploadInputRoot) {
  const d = photoUploadInputRoot.dataset;
  useQuasar(createApp(PhotoUploadInput, {
    label:  d.label  ?? "",
    hint:   d.hint   ?? "",
    error:  d.error  ?? "",
  })).mount(photoUploadInputRoot);
}

const documentUploadFormRoot = document.querySelector("[data-vue-document-upload-form]");
if (documentUploadFormRoot) {
  const d = documentUploadFormRoot.dataset;
  useQuasar(createApp(DocumentUploadForm, {
    labelDocumentType:   d.labelDocumentType   ?? "",
    labelFile:           d.labelFile           ?? "",
    labelAdd:            d.labelAdd            ?? "",
    labelRemove:         d.labelRemove         ?? "",
    hintFile:            d.hintFile            ?? "",
    documentTypesJson:   d.documentTypesJson   ?? "[]",
    initialRowsJson:     d.initialRowsJson     ?? "[]",
    errors:              d.errors              ?? "{}",
  })).mount(documentUploadFormRoot);
}

// ── Legal document version form ────────────────────────────────

const legalVersionFormRoot = document.querySelector("[data-vue-legal-version-form]");
if (legalVersionFormRoot) {
  const d = legalVersionFormRoot.dataset;
  useQuasar(createApp(LegalDocumentVersionForm, {
    documentId:        Number(d.documentId        ?? 0),
    versionId:         Number(d.versionId         ?? 0),
    versionLabel:      d.versionLabel             ?? "",
    primaryLanguageId: Number(d.primaryLanguageId ?? 0),
    translationsJson:  d.translationsJson         ?? "[]",
    actionUrl:         d.actionUrl                ?? "",
    antiforgeryToken:  d.antiforgeryToken         ?? "",
    versionLabelLabel: d.versionLabelLabel        ?? "",
    titleLabel:        d.titleLabel               ?? "",
    bodyLabel:         d.bodyLabel                ?? "",
    saveLabel:         d.saveLabel                ?? "",
    requiredMsg:       d.requiredMsg              ?? "",
    primaryRequiredMsg:d.primaryRequiredMsg       ?? "",
  })).mount(legalVersionFormRoot);
}

// ── Legal publish form (dedicated publish page) ────────────────

const legalPublishFormRoot = document.querySelector("[data-vue-legal-publish-form]");
if (legalPublishFormRoot) {
  const d = legalPublishFormRoot.dataset;
  useQuasar(createApp(LegalPublishForm, {
    actionUrl:        d.actionUrl        ?? "",
    cancelUrl:        d.cancelUrl        ?? "",
    antiforgeryToken: d.antiforgeryToken ?? "",
    documentId:       Number(d.documentId ?? 0),
    versionId:        Number(d.versionId  ?? 0),
    minDate:          d.minDate          ?? "",
    dateLabel:        d.dateLabel         ?? "",
    publishLabel:     d.publishLabel      ?? "",
    cancelLabel:      d.cancelLabel       ?? "",
    requiredMsg:      d.requiredMsg       ?? "",
    hint:             d.hint              ?? "",
  })).mount(legalPublishFormRoot);
}

// ── Address map view (read-only) ────────────────────────────────

document.querySelectorAll("[data-vue-address-map-view]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(AddressMapView, {
    lat:   d.lat   ?? "",
    lng:   d.lng   ?? "",
    zoom:  d.zoom  ?? "16",
  })).mount(el);
});

// ── WEB-010 single form fields ─────────────────────────────────
// One mount per element. Bounded by the page: at most a handful per view — a field that needs a
// row of a table is a field that needs its own page, and every one of them has one.
document.querySelectorAll("[data-vue-form-field]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(PcFormField, {
    name:         d.name  ?? "",
    // The dataset key stays `data-value`; the prop is `initialValue` because the component
    // seeds a ref from it once and never watches it (house style: initial* for a seed prop).
    initialValue: d.value ?? "",
    type:         d.type        ?? "text",
    label:        d.label       ?? "",
    placeholder:  d.placeholder ?? "",
    hint:         d.hint        ?? "",
    maxlength:    d.maxlength   ?? "",
    rows:         d.rows        ?? "4",
    min:          d.min         ?? "",
    max:          d.max         ?? "",
    step:         d.step        ?? "",
    requiredMsg:  d.requiredMsg ?? "",
  })).mount(el);
});

document.querySelectorAll("[data-vue-file-field]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(PcFileField, {
    name:        d.name        ?? "",
    accept:      d.accept      ?? "",
    label:       d.label       ?? "",
    hint:        d.hint        ?? "",
    requiredMsg: d.requiredMsg ?? "",
  })).mount(el);
});

document.querySelectorAll("[data-vue-checkbox-group]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(PcCheckboxGroup, {
    name:                d.name         ?? "",
    groupsJson:          d.groupsJson   ?? "[]",
    initialSelectedJson: d.selectedJson ?? "[]",
    // The island emits the section marker itself; a Razor-rendered one would post `true`
    // even when this mount fails, and the save path would delete every row.
    markerName:          d.markerName   ?? "",
  })).mount(el);
});

// ── Disputed-visit settlement ──────────────────────────────────
// One mount, on the settlement page's own form. Every label arrives from Razor; the amounts are
// the invariant strings Razor rendered and are carried through untouched.
document.querySelectorAll("[data-vue-resolve-visit-panel]").forEach(el => {
  const d = el.dataset;
  useQuasar(createApp(ResolveVisitPanel, {
    initialCustomerAmount:  d.customerAmount     ?? "",
    customerMax:            d.customerMax        ?? "",
    initialCaregiverAmount: d.caregiverAmount    ?? "",
    caregiverMax:           d.caregiverMax       ?? "",
    invoiceNote:            d.invoiceNote        ?? "",
    labelRefundAmount:      d.labelRefundAmount  ?? "",
    labelClawback:          d.labelClawback      ?? "",
    labelClawbackAmount:    d.labelClawbackAmount ?? "",
    labelReason:            d.labelReason        ?? "",
  })).mount(el);
});

installConfirmInterceptor(document, confirmDialogReady);
